// src/components/EditSpecialLoad.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "../axiosConfig";
import Select from "react-select";
import { FaCalendarAlt, FaClock, FaCheckCircle } from "react-icons/fa";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import "../styles/EditSpecialLoad.css";
import CreatableSelect from "react-select/creatable";
import { customSelectStyles } from "../styles/reactSelectStyles.js";

// === Helpers ===
const CURRENCY_OPTIONS = [
  { value: "EUR", label: "EUR" },
  { value: "USD", label: "USD" },
  { value: "GBP", label: "GBP" },
];

const toDateInput = (d) => {
  if (!d) return "";
  const s = typeof d === "string" ? d : new Date(d).toISOString();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
};

const uniqBy = (arr, keyFn) => {
  const seen = new Set();
  return arr.filter((x) => {
    const k = keyFn(x);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const getTsCounter = (obj) =>
  obj?.transportSession?.transportSessionNumber ??
  obj?.transportSession?.sessionNumber ??
  obj?.transportSession?.tsNumber ??
  obj?.transportSession?.counter ??
  obj?.transportsessionNumber ??
  obj?.tsNumber ??
  obj?.transportSessionCounter ??
  obj?.tsCounter ??
  obj?.transport_session_counter ??
  obj?.tsUiCounter ??
  null;

const getLoadCounter = (obj) =>
  obj?.loadNumber ?? obj?.loadCounter ?? obj?.load_counter ?? obj?.counter ?? obj?.uiCounter ?? null;

const displayAuditEmail = (value) => {
  const email = String(value || "").trim();
  return email || "—";
};

const seedPlaceOptionsFromPrefill = (pf) => {
  if (!pf) return [];
  const pts = [...(pf.pickupPoints || []), ...(pf.deliveryPoints || [])];
  const opts = pts
    .map((p) => {
      const id = p.placeId ?? p.place?.id ?? p.location ?? "";
      const label = p.place?.placeName || p.location || "";
      return id && label ? { value: id, label, fullData: p.place ?? null } : null;
    })
    .filter(Boolean);
  return uniqBy(opts, (o) => String(o.value));
};

const makeOption = (id, label, fallbackPrefix = "ID") =>
  id ? { value: Number(id), label: label || `${fallbackPrefix} ${id}` } : null;

const EditSpecialLoad = () => {
  const { t } = useTranslation("edit_special_load");

const isValidEmail = (value) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
};

const emailsToOptions = (raw) => {
  if (!raw) return [];

  const arr = Array.isArray(raw)
    ? raw
    : String(raw)
        .split(",")
        .map((x) => x.trim());

  return arr
    .map((email) => String(email || "").trim().toLowerCase())
    .filter((email) => email && isValidEmail(email))
    .slice(0, 3)
    .map((email) => ({ value: email, label: email }));
};

// 🆕 Loading access options (match EditLoadBearers logic)
const loadingAccessOptions = [
  { value: "SIDE", label: t("loadingAccess.options.side", { defaultValue: "Side" }) },
  { value: "REAR", label: t("loadingAccess.options.rear", { defaultValue: "Rear" }) },
  { value: "TOP",  label: t("loadingAccess.options.top",  { defaultValue: "Top" }) },
  { value: "DOCK", label: t("loadingAccess.options.dock", { defaultValue: "Dock" }) },
  { value: "ANY",  label: t("loadingAccess.options.any",  { defaultValue: "Any" }) },
];

// 🆕 Handling equipment options (match new Load Bearer locales)
const handlingEquipmentOptions = [
  { value: "FORKLIFT", label: t("fields.handlingEquipment.forklift", { defaultValue: "Forklift" }) },
  { value: "PALLET_JACK", label: t("fields.handlingEquipment.palletJack", { defaultValue: "Pallet jack" }) },
  { value: "ELECTRIC_PALLET_JACK", label: t("fields.handlingEquipment.electricPalletJack", { defaultValue: "Electric pallet jack" }) },
  { value: "CRANE", label: t("fields.handlingEquipment.crane", { defaultValue: "Crane" }) },
  { value: "LOADING_RAMP", label: t("fields.handlingEquipment.loadingRamp", { defaultValue: "Loading ramp" }) },
  { value: "TAIL_LIFT", label: t("fields.handlingEquipment.tailLift", { defaultValue: "Tail lift" }) },
  { value: "HAND_LOAD", label: t("fields.handlingEquipment.handLoad", { defaultValue: "Hand load" }) },
  { value: "CONVEYOR", label: t("fields.handlingEquipment.conveyor", { defaultValue: "Conveyor belt" }) },
  { value: "OTHER", label: t("fields.handlingEquipment.other", { defaultValue: "Other" }) },
];

// 🆕 Trailer type options (load-level) — KEEP IN SYNC with backend enum
const trailerTypeOptions = [
  { value: "ANY", label: t("fields.trailerTypeOptions.any", { defaultValue: "Any" }) },

  { value: "TAUTLINER", label: t("fields.trailerTypeOptions.tautliner", { defaultValue: "Tautliner / Curtain" }) },
  { value: "REEFER", label: t("fields.trailerTypeOptions.reefer", { defaultValue: "Reefer / Frigo" }) },
  { value: "BOX", label: t("fields.trailerTypeOptions.box", { defaultValue: "Box" }) },
  { value: "FLATBED", label: t("fields.trailerTypeOptions.flatbed", { defaultValue: "Flatbed" }) },
  { value: "LOWBED", label: t("fields.trailerTypeOptions.lowbed", { defaultValue: "Lowbed" }) },
  { value: "TANKER", label: t("fields.trailerTypeOptions.tanker", { defaultValue: "Tanker" }) },
  { value: "TIPPER", label: t("fields.trailerTypeOptions.tipper", { defaultValue: "Tipper" }) },
  { value: "CONTAINER_CHASSIS", label: t("fields.trailerTypeOptions.containerChassis", { defaultValue: "Container chassis" }) },
  { value: "MEGA", label: t("fields.trailerTypeOptions.mega", { defaultValue: "Mega" }) },

  { value: "OTHER", label: t("fields.trailerTypeOptions.other", { defaultValue: "Other" }) },
];

  const { loadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill ?? null;

  // ⚡ Inicializa já a UI com o prefill (render imediato)
  const [load, setLoad] = useState(prefill || null);
  const [length, setLength] = useState(prefill?.length ?? "");
  const [height, setHeight] = useState(prefill?.height ?? "");
  const [width, setWidth] = useState(prefill?.width ?? "");
  const [weight, setWeight] = useState(prefill?.weight ?? "");
  const [price, setPrice] = useState(prefill?.price ?? "");
  const [priceError, setPriceError] = useState("");
  const [reference, setReference] = useState(prefill?.reference ?? "");
  const [referenceError, setReferenceError] = useState("");
  // 🆕 Trailer type (load-level)
  const [trailerType, setTrailerType] = useState(prefill?.trailerType ?? "");
  const [trailerTypeOther, setTrailerTypeOther] = useState(prefill?.trailerTypeOther ?? "");
  const [trailerTypeError, setTrailerTypeError] = useState("");
  const [trailerTypeOtherError, setTrailerTypeOtherError] = useState("");

  // 🆕 Merchandise (required)
  const [merchandise, setMerchandise] = useState(prefill?.merchandise ?? "");
  const [merchandiseError, setMerchandiseError] = useState("");
  
  // 🆕 Price for subcontracted (optional, only when external carrier selected)
  const [price4subcontrated, setPrice4subcontrated] = useState(prefill?.price4subcontrated ?? "");
  const [price4subcontratedError, setPrice4subcontratedError] = useState("");
  
  const [priceCurrency, setPriceCurrency] = useState(load?.priceCurrency || "EUR");
  const [notes4driver, setNotes4driver] = useState(prefill?.notes4driver ?? "");
  const [notes4driverError, setNotes4driverError] = useState("");
  // 🆕 Notes for subcontracted (optional, only when external carrier selected)
  const [notes4subcontracted, setNotes4subcontracted] = useState(prefill?.notes4subcontracted ?? "");
  const [notes4subcontractedError, setNotes4subcontractedError] = useState("");

  const [selectedSubcontractedEmails, setSelectedSubcontractedEmails] = useState(() => {
    const raw =
      prefill?.subcontractedEmails ??
      prefill?.subcontractedEmail ??
      load?.subcontractedEmails ??
      load?.subcontractedEmail ??
      [];
  
    if (Array.isArray(raw)) {
      return raw
        .map((email) => {
          const e = String(email || "").trim().toLowerCase();
          return e && isValidEmail(e) ? { value: e, label: e } : null;
        })
        .filter(Boolean)
        .slice(0, 3);
    }
  
    if (typeof raw === "string" && raw.trim()) {
      const e = raw.trim().toLowerCase();
      return isValidEmail(e) ? [{ value: e, label: e }] : [];
    }
  
    return [];
  });

  // 🧾 Current company (tenant) for comparison + label internal
  const [companyId, setCompanyId] = useState(null);
  const [companyName, setCompanyName] = useState("");

  const [ready, setReady] = useState(() => !!prefill);

  // “chip” de carrier (transportador)
  const [carrierPrefill, setCarrierPrefill] = useState(() =>
    prefill?.carrierId
      ? {
          value: Number(prefill.carrierId),
          label:
            prefill?.carrierName ||
            prefill?.transportCarrierName ||
            prefill?.carrier?.companyName ||
            t("fallbacks.carrier"),
        }
      : null
  );

  // 👉 placeOptions já nasce com “chips” do prefill
  const [placeOptions, setPlaceOptions] = useState(() => seedPlaceOptionsFromPrefill(prefill));

  const [pickupLocations, setPickupLocations] = useState(() => {
    if (Array.isArray(prefill?.pickupPoints)) {
      return prefill.pickupPoints.map((p) => ({
        location: p.location || "",
        date: toDateInput(p.date),
        hour: p.hour || "",

        // 🆕 point-level dimensions
        length: p.length ?? "",
        width: p.width ?? "",
        height: p.height ?? "",
        lengthError: "",
        widthError: "",
        heightError: "",

        placeId: p.placeId ?? null,
        address: p.address || "",
        postalCode: p.postalCode || "",
        city: p.city || "",
        country: p.country || "",
        gMapsLink: p.gMapsLink || "",
        coordinates: p.coordinates || "",
        contactPhone: p.contactPhone || "",

        // 🆕 reference (optional)
        reference: p.reference || "",

        // 🆕 NEW
        loadingAccess: p.loadingAccess ?? null,
        // 🆕 NEW: handling equipment (per point)
        handlingEquipment: p.handlingEquipment ?? null,
        handlingEquipmentOther: p.handlingEquipmentOther ?? "",

        showMoreFields: false,
      }));
    }
    return [
      {
        location: "",
        date: "",
        hour: "",

        // 🆕 point-level dimensions
        length: "",
        width: "",
        height: "",
        lengthError: "",
        widthError: "",
        heightError: "",

        placeId: null,
        address: "",
        postalCode: "",
        city: "",
        country: "",
        gMapsLink: "",
        coordinates: "",
        contactPhone: "",

        // 🆕 reference (optional)
        reference: "",

        // 🆕 NEW
        loadingAccess: null,

        // 🆕 NEW: handling equipment (per point)
        handlingEquipment: null,
        handlingEquipmentOther: "",

        showMoreFields: false,
      },
    ];
  });

  const [deliveryLocations, setDeliveryLocations] = useState(() => {
    if (Array.isArray(prefill?.deliveryPoints)) {
      return prefill.deliveryPoints.map((p) => ({
        location: p.location || "",
        date: toDateInput(p.date),
        hour: p.hour || "",

        // 🆕 point-level dimensions
        length: p.length ?? "",
        width: p.width ?? "",
        height: p.height ?? "",
        lengthError: "",
        widthError: "",
        heightError: "",

        placeId: p.placeId ?? null,
        address: p.address || "",
        postalCode: p.postalCode || "",
        city: p.city || "",
        country: p.country || "",
        gMapsLink: p.gMapsLink || "",
        coordinates: p.coordinates || "",
        contactPhone: p.contactPhone || "",

        // 🆕 reference (optional)
        reference: p.reference || "",

        loadingAccess: p.loadingAccess ?? null,
        showMoreFields: false,
      }));
    }
    return [
      {
        location: "",
        date: "",
        hour: "",

        // 🆕 point-level dimensions
        length: "",
        width: "",
        height: "",
        lengthError: "",
        widthError: "",
        heightError: "",

        placeId: null,
        address: "",
        postalCode: "",
        city: "",
        country: "",
        gMapsLink: "",
        coordinates: "",
        contactPhone: "",

        // 🆕 reference (optional)
        reference: "",

        loadingAccess: null,
        showMoreFields: false,
      },
    ];
  });

  const [lengthError, setLengthError] = useState("");
  const [heightError, setHeightError] = useState("");
  const [widthError, setWidthError] = useState("");
  const [weightError, setWeightError] = useState("");
  const [specialDimError, setSpecialDimError] = useState("");
  const [clientType, setClientType] = useState(prefill?.clientType ?? "");
  const [industryClientId, setIndustryClientId] = useState(prefill?.industryClientId ?? "");
  const [carrierClientId, setCarrierClientId] = useState(prefill?.carrierClientId ?? "");

  const [industryOptions, setIndustryOptions] = useState([]);
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientContactEmails, setSelectedClientContactEmails] = useState([]);
  const [clientTypeError, setClientTypeError] = useState("");
  const [clientSelectionError, setClientSelectionError] = useState("");

  const extractClientEmails = (clientObj) => {
    if (!clientObj || typeof clientObj !== "object") return [];

    const found = new Set();

    const addEmail = (value) => {
      const email = String(value || "").trim().toLowerCase();
      if (!email) return;
      if (!isValidEmail(email)) return;
      found.add(email);
    };

    const contactsRaw = clientObj.contacts;
    let contacts = [];

    if (Array.isArray(contactsRaw)) {
      contacts = contactsRaw;
    } else if (typeof contactsRaw === "string") {
      try {
        const parsed = JSON.parse(contactsRaw);
        if (Array.isArray(parsed)) contacts = parsed;
      } catch {
        contacts = [];
      }
    }

    contacts.forEach((contact) => {
      addEmail(contact?.contactEmail);
    });

    return Array.from(found).map((email) => ({
      value: email,
      label: email,
    }));
  };

  const selectedClientContactEmailOptions = useMemo(() => {
    if (!clientType) return [];

    const selectedId =
      clientType === "industry"
        ? selectedClient?.value || industryClientId
        : selectedClient?.value || carrierClientId;

    if (!selectedId) return [];

    const sourceList = clientType === "industry" ? industryOptions : carrierOptions;
    const fullClient = (sourceList || []).find(
      (c) => String(c.value || c.id) === String(selectedId)
    );

    const rawClient =
      clientType === "industry"
        ? (Array.isArray(industryOptions) ? industryOptions.find((c) => String(c.value || c.id) === String(selectedId)) : null)
        : (Array.isArray(carrierOptions) ? carrierOptions.find((c) => String(c.value || c.id) === String(selectedId)) : null);

    return extractClientEmails(fullClient?.raw || rawClient?.raw || null);
  }, [clientType, selectedClient, industryClientId, carrierClientId, industryOptions, carrierOptions]);

  const multiSelectHorizontalStyles = useMemo(() => {
    const base = customSelectStyles || {};

    return {
      ...base,

      control: (provided, state) => ({
        ...(base.control ? base.control(provided, state) : provided),
        minHeight: 44,
        height: "auto",
        overflow: "hidden",
        alignItems: "center",
      }),

      valueContainer: (provided, state) => ({
        ...(base.valueContainer ? base.valueContainer(provided, state) : provided),

        // ✅ tags sempre numa linha
        flexWrap: "nowrap",

        // ✅ scroll horizontal quando há várias opções
        overflowX: "auto",
        overflowY: "hidden",
        whiteSpace: "nowrap",

        // ✅ altura/alinhamento consistente
        alignItems: "center",
        paddingTop: 6,
        paddingBottom: 6,

        // ✅ espaço para clear + seta
        paddingRight: 72,

        gap: 6,
      }),

      multiValue: (provided, state) => ({
        ...(base.multiValue ? base.multiValue(provided, state) : provided),
        flex: "0 0 auto",
        maxWidth: "none",
        margin: 0,
      }),

      multiValueLabel: (provided, state) => ({
        ...(base.multiValueLabel ? base.multiValueLabel(provided, state) : provided),
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }),

      indicatorsContainer: (provided, state) => ({
        ...(base.indicatorsContainer ? base.indicatorsContainer(provided, state) : provided),
        alignItems: "center",
        paddingTop: 0,
      }),

      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
    };
  }, []);

  const [formSubmitted, setFormSubmitted] = useState(false);
  const showErrors = formSubmitted;

  // 🆕 handler Loading Access (dentro do componente)
  const handleLoadingAccessChange = (index, opt, isDelivery = false) => {
    const value = opt?.value ? String(opt.value).trim().toUpperCase() : null;

    if (isDelivery) {
      setDeliveryLocations((prev) => {
        const updated = [...prev];
        if (!updated[index]) return prev;

        updated[index] = {
          ...updated[index],
          loadingAccess: value,
          loadingAccessError: false,
        };

        return updated;
      });

      // opcional: se estiveres a guardar loadingAccess em invalidFields
      if (showErrors) {
        setInvalidFields((prev) => {
          const next = { ...prev };
          const d = { ...(next.deliveries || {}) };

          if (d[index]) {
            delete d[index].loadingAccess;
            if (Object.keys(d[index]).length === 0) delete d[index];
          }

          next.deliveries = d;
          return next;
        });
      }
    } else {
      setPickupLocations((prev) => {
        const updated = [...prev];
        if (!updated[index]) return prev;

        updated[index] = {
          ...updated[index],
          loadingAccess: value,
          loadingAccessError: false,
        };

        return updated;
      });

      // opcional: se estiveres a guardar loadingAccess em invalidFields
      if (showErrors) {
        setInvalidFields((prev) => {
          const next = { ...prev };
          const p = { ...(next.pickups || {}) };

          if (p[index]) {
            delete p[index].loadingAccess;
            if (Object.keys(p[index]).length === 0) delete p[index];
          }

          next.pickups = p;
          return next;
        });
      }
    }
  };

  // 🆕 handler Handling Equipment
  const handleHandlingEquipmentChange = (index, opt, isDelivery = false) => {
    const value = opt?.value ? String(opt.value).trim().toUpperCase() : null;

    const apply = (prev) => {
      const updated = [...prev];
      if (!updated[index]) return prev;

      const isOther = value === "OTHER";

      updated[index] = {
        ...updated[index],
        handlingEquipment: value,
        // se não for OTHER, limpa o texto e o erro
        handlingEquipmentOther: isOther ? (updated[index].handlingEquipmentOther || "") : "",
        handlingEquipmentOtherError: false,
      };

      return updated;
    };

    if (isDelivery) setDeliveryLocations(apply);
    else setPickupLocations(apply);

    if (showErrors) {
      setInvalidFields((prev) => {
        const next = { ...prev };
        const bucket = isDelivery ? { ...(next.deliveries || {}) } : { ...(next.pickups || {}) };

        if (bucket[index]) {
          delete bucket[index].handlingEquipment;
          delete bucket[index].handlingEquipmentOther;
          if (Object.keys(bucket[index]).length === 0) delete bucket[index];
        }

        if (isDelivery) next.deliveries = bucket;
        else next.pickups = bucket;

        return next;
      });
    }
  };

  // 🆕 Trailer Type handlers (load-level)
  const handleTrailerTypeChange = (opt) => {
    const value = opt?.value ? String(opt.value).trim().toUpperCase() : "";

    setTrailerType(value);
    setTrailerTypeError("");

    // se sair de OTHER, limpa o texto
    if (value !== "OTHER") {
      setTrailerTypeOther("");
      setTrailerTypeOtherError("");
    }
  };

  const handleTrailerTypeOtherChange = (e) => {
    const v = e.target.value ?? "";
    setTrailerTypeOther(v);
    if (String(v).trim()) setTrailerTypeOtherError("");
  };

  const [carrierId, setCarrierId] = useState(prefill?.carrierId ?? "");
  const [carrierOptsRaw, setCarrierOptsRaw] = useState([]); // ✅ only backend carriers

  const dateRefs = useRef([]);
  const hourRefs = useRef([]);
  const deliveryDateRefs = useRef([]);
  const deliveryHourRefs = useRef([]);

  const [fieldValidationError, setFieldValidationError] = useState("");
  const [invalidFields, setInvalidFields] = useState({ pickups: {}, deliveries: {} });
  const [deliveryBeforePickupErrorIndexes, setDeliveryBeforePickupErrorIndexes] = useState({
    date: [],
    hour: [],
  });

  const [pickUpFieldError, setPickUpFieldError] = useState("");
  const [deliveryFieldError, setDeliveryFieldError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);
  const [carrierTouched, setCarrierTouched] = useState(false);

  const handleCloseSuccessPopup = () => {
    setSuccessPopupOpen(false);
    navigate("/loads");
  };

  const INTERNAL_CARRIER_VALUE = "__internal__";

  // ✅ NORMAL limits (standard vehicle envelope)
  const NORMAL_MAX = {
    length: 13.6,
    height: 2.7,
    width: 2.5,
  };

  // ✅ SPECIAL ranges (your special load constraints)
  const SPECIAL_RANGE = {
    length: { min: 13.61, max: 20 },
    height: { min: 2.71, max: 5 },
    width: { min: 2.51, max: 4 },
  };

  const inRange = (n, a, b) => Number.isFinite(n) && n >= a && n <= b;

  const isSpecialLength = (n) => inRange(n, SPECIAL_RANGE.length.min, SPECIAL_RANGE.length.max);
  const isSpecialHeight = (n) => inRange(n, SPECIAL_RANGE.height.min, SPECIAL_RANGE.height.max);
  const isSpecialWidth  = (n) => inRange(n, SPECIAL_RANGE.width.min,  SPECIAL_RANGE.width.max);

  const isNormalLength = (n) => Number.isFinite(n) && n > 0 && n <= NORMAL_MAX.length;
  const isNormalHeight = (n) => Number.isFinite(n) && n > 0 && n <= NORMAL_MAX.height;
  const isNormalWidth  = (n) => Number.isFinite(n) && n > 0 && n <= NORMAL_MAX.width;

  const isValidSpecialLoadLength = (n) => isNormalLength(n) || isSpecialLength(n);
  const isValidSpecialLoadHeight = (n) => isNormalHeight(n) || isSpecialHeight(n);
  const isValidSpecialLoadWidth = (n) => isNormalWidth(n) || isSpecialWidth(n);

  const getPointDimensionError = (field, value) => {
    const n = Number(value);

    if (value === "" || value == null) return "";
    if (!Number.isFinite(n) || n <= 0) {
      if (field === "length") return t("validation.lengthRequired");
      if (field === "height") return t("validation.heightRequired");
      if (field === "width") return t("validation.widthRequired");
      return t("errors.allRequiredFields");
    }

    if (field === "length" && !isValidSpecialLoadLength(n)) {
      return t("validation.specialLengthRange", { min: 13.61, max: 20 });
    }

    if (field === "height" && !isValidSpecialLoadHeight(n)) {
      return t("validation.specialHeightRange", { min: 2.71, max: 5 });
    }

    if (field === "width" && !isValidSpecialLoadWidth(n)) {
      return t("validation.specialWidthRange", { min: 2.51, max: 4 });
    }

    return "";
  };

  const calculatePointDimensionTotals = (points = []) => {
    return points.reduce(
      (acc, point) => {
        const pointLength = Number(point.length || 0);
        const pointWidth = Number(point.width || 0);
        const pointHeight = Number(point.height || 0);

        return {
          length: acc.length + (Number.isFinite(pointLength) ? pointLength : 0),
          width: Math.max(acc.width, Number.isFinite(pointWidth) ? pointWidth : 0),
          height: Math.max(acc.height, Number.isFinite(pointHeight) ? pointHeight : 0),
        };
      },
      { length: 0, width: 0, height: 0 }
    );
  };

  const pickupDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(pickupLocations);
  }, [pickupLocations]);

  const deliveryDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(deliveryLocations);
  }, [deliveryLocations]);

  const pointDimensionSummary = useMemo(() => {
    const pickupLength = Number(pickupDimensionTotals.length || 0);
    const deliveryLength = Number(deliveryDimensionTotals.length || 0);

    const lengthMatches =
      Math.round(pickupLength * 100) === Math.round(deliveryLength * 100);

    const totalLength = pickupLength;
    const totalWidth = Math.max(pickupDimensionTotals.width, deliveryDimensionTotals.width);
    const totalHeight = Math.max(pickupDimensionTotals.height, deliveryDimensionTotals.height);

    const hasSpecialDimension =
      isSpecialLength(totalLength) ||
      isSpecialHeight(totalHeight) ||
      isSpecialWidth(totalWidth);

    return {
      length: totalLength,
      pickupLength,
      deliveryLength,
      width: totalWidth,
      height: totalHeight,
      lengthMatches,
      hasSpecialDimension,
    };
  }, [pickupDimensionTotals, deliveryDimensionTotals]);

  useEffect(() => {
    const nextLength = pointDimensionSummary.length > 0
      ? pointDimensionSummary.length.toFixed(2)
      : "";

    const nextWidth = pointDimensionSummary.width > 0
      ? pointDimensionSummary.width.toFixed(2)
      : "";

    const nextHeight = pointDimensionSummary.height > 0
      ? pointDimensionSummary.height.toFixed(2)
      : "";

    setLength(nextLength);
    setWidth(nextWidth);
    setHeight(nextHeight);

    if (nextLength) setLengthError("");
    if (nextWidth) setWidthError("");
    if (nextHeight) setHeightError("");

    if (pointDimensionSummary.hasSpecialDimension && specialDimError) {
      setSpecialDimError("");
    }
  }, [
    pointDimensionSummary.length,
    pointDimensionSummary.width,
    pointDimensionSummary.height,
    pointDimensionSummary.hasSpecialDimension,
    specialDimError,
  ]);

  // 🆕 show notes4subcontracted ONLY if carrierId differs from companyId
  const showNotes4subcontracted = useMemo(() => {
    if (carrierId == null || String(carrierId).trim() === "") return false;

    // handle your internal sentinel if it ever appears here
    if (String(carrierId) === INTERNAL_CARRIER_VALUE) return false;

    if (companyId != null && String(carrierId) === String(companyId)) return false;

    return true;
  }, [carrierId, companyId]);

  const selectedSubcontractedEmailOptions = useMemo(() => {
    if (!showNotes4subcontracted) return [];
    if (!carrierId || String(carrierId) === INTERNAL_CARRIER_VALUE) return [];
  
    const fullCarrier = (carrierOptions || []).find(
      (c) => String(c.value || c.id) === String(carrierId)
    );
  
    return extractClientEmails(fullCarrier?.raw || null);
  }, [showNotes4subcontracted, carrierId, carrierOptions, INTERNAL_CARRIER_VALUE]);

  // ✅ internal carrier option (dynamic label)
  const internalCarrierOption = useMemo(() => {
    const name = String(companyName || "").trim();
    const label = name
      ? `${name} (internal)`
      : t("fallbacks.internalCarrier", { defaultValue: "Internal (carrier)" });

    // UI sentinel (nunca um id real)
    return { value: INTERNAL_CARRIER_VALUE, label };
  }, [companyName, t]);

  // ✅ final carriers list for Select (internal first)
  const carrierOptionsForSelect = useMemo(() => {
    return [
      internalCarrierOption,
      ...(Array.isArray(carrierOptsRaw) ? carrierOptsRaw : []),
    ];
  }, [internalCarrierOption, carrierOptsRaw]);

  // 🧼 if user switches back to internal carrier, clear subcontracted notes
  useEffect(() => {
    if (!showNotes4subcontracted) {
      setNotes4subcontracted("");
      setNotes4subcontractedError("");
  
      setPrice4subcontrated("");
      setPrice4subcontratedError("");
  
      setSelectedSubcontractedEmails([]);
      setPriceError("");
    }
  }, [showNotes4subcontracted]); 

  const tsCounter = getTsCounter(load ?? prefill);
  const loadCounterVal = getLoadCounter(load ?? prefill);

  // 🔄 Refs quando arrays mudam
  useEffect(() => {
    deliveryDateRefs.current = deliveryLocations.map((_, i) => deliveryDateRefs.current[i] || null);
    deliveryHourRefs.current = deliveryLocations.map((_, i) => deliveryHourRefs.current[i] || null);
  }, [deliveryLocations]);

  // 🆕 fetch companyId + companyName from whoami (tenant)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get("/auth/whoami");
        if (!alive) return;

        const cid =
          res.data?.companyId ??
          res.data?.company_id ??
          res.data?.company?.id ??
          res.data?.userCompanyRaw ??
          res.data?.reqCompanyId ??
          null;

        const cname =
          res.data?.companyName ??
          res.data?.company?.name ??
          res.data?.company?.companyName ??
          res.data?.company_name ??
          "";

        setCompanyId(cid != null ? String(cid) : null);
        setCompanyName(String(cname || "").trim());
      } catch (e) {
        console.warn("whoami fetch failed:", e?.response?.status);
        setCompanyId(null);
        setCompanyName("");
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    dateRefs.current = pickupLocations.map((_, i) => dateRefs.current[i] || null);
    hourRefs.current = pickupLocations.map((_, i) => hourRefs.current[i] || null);
  }, [pickupLocations]);

  useEffect(() => {
    if (!showNotes4subcontracted) {
      setSelectedSubcontractedEmails([]);
      return;
    }
  
    if (!selectedSubcontractedEmailOptions.length) {
      setSelectedSubcontractedEmails([]);
      return;
    }
  
    setSelectedSubcontractedEmails((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) {
        return selectedSubcontractedEmailOptions.slice(0, 3);
      }
  
      const allowed = new Set(
        selectedSubcontractedEmailOptions.map((opt) => String(opt.value))
      );
  
      return prev.filter((opt) => allowed.has(String(opt.value))).slice(0, 3);
    });
  }, [showNotes4subcontracted, selectedSubcontractedEmailOptions]);

  // ⚡ Buscar PLACES em background e fundir com os chips do prefill
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get("/places");
        if (!alive) return;

        const fetched = (res.data || []).map((p) => ({ value: p.id, label: p.placeName, fullData: p }));
        const norm = (s) => (s || "").trim().toLowerCase();

        setPlaceOptions((prev) => {
          const merged = [...fetched, ...(prev || [])];
          const seen = new Set();
          return merged.filter((o) => {
            const k = norm(o.label);
            if (!k || seen.has(k)) return false;
            seen.add(k);
            return true;
          });
        });
      } catch (err) {
        console.error("❌ Error fetching places:", err);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // ⚡ Buscar clients/carriers em paralelo
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [industries, carriersRes] = await Promise.allSettled([
          axios.get("/industries"),
          axios.get("/carriers"),
        ]);
        if (!alive) return;

        if (industries.status === "fulfilled") {
          const opts = (industries.value.data || []).map((i) => ({
            value: i.id,
            label: i.companyName,
            raw: i,
          }));
          setIndustryOptions(opts);
        }

        if (carriersRes.status === "fulfilled") {
          const opts = (carriersRes.value.data || []).map((c) => ({
            value: c.id,
            label: c.companyName,
            raw: c,
          }));
          setCarrierOptions(opts);

          const carrierSelectOpts = (carriersRes.value.data || []).map((c) => ({
            value: c.id,
            label: c.companyName,
          }));
          setCarrierOptsRaw(carrierSelectOpts); // ✅ raw only
        }
      } catch (e) {
        console.error("❌ Error fetching clients:", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // 🔄 Refresh silencioso do load "oficial"
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`/loads/${loadId}/editloads`);
        if (!alive) return;

        if (!data.specialLoad) {
          setLengthError(t("errors.notSpecialLoad"));
          return;
        }

        setSelectedClientContactEmails((prev) => {
          if (Array.isArray(prev) && prev.length > 0) return prev;
          return emailsToOptions(data.clientContactEmails);
        });

        setLoad((prev) => ({
          ...(prev || {}),
          ...(data || {}),
          createdByEmail: data?.createdByEmail ?? prev?.createdByEmail ?? "",
          updatedByEmail: data?.updatedByEmail ?? prev?.updatedByEmail ?? "",
        }));
        setLength(data.length || "");
        setHeight(data.height || "");
        setWidth(data.width || "");
        setWeight(data.weight || "");

        // 🆕 reference (optional)
        setReference((prev) =>
          typeof prev === "string" && prev.length ? prev : (data.reference ?? "")
        );

        // 🆕 trailer type from backend
        setTrailerType((prev) => (String(prev || "").trim() ? prev : (data.trailerType ?? "")));
        setTrailerTypeOther((prev) =>
          String(prev || "").trim() ? prev : (data.trailerTypeOther ?? "")
        );

        // 🆕 set merchandise (prefer "data.merchandise", fallback "data.load?.merchandise" se existir)
        setMerchandise((prev) => (typeof prev === "string" && prev.length ? prev : (data.merchandise ?? "")));

        setPrice4subcontrated(
          data.price4subcontrated != null && data.price4subcontrated !== ""
            ? String(data.price4subcontrated)
            : ""
        );        

        setNotes4driver((prev) =>
          typeof prev === "string" && prev.length ? prev : (data.notes4driver ?? "")
        );

        setNotes4subcontracted((prev) =>
          typeof prev === "string" && prev.length ? prev : (data.notes4subcontracted ?? "")
        );

        setSelectedSubcontractedEmails((prev) => {
          if (Array.isArray(prev) && prev.length > 0) return prev;
        
          const raw =
            data.subcontractedEmails ??
            data.subcontractedEmail ??
            [];
        
          if (Array.isArray(raw)) {
            return raw
              .map((email) => {
                const e = String(email || "").trim().toLowerCase();
                return e && isValidEmail(e) ? { value: e, label: e } : null;
              })
              .filter(Boolean)
              .slice(0, 3);
          }
        
          if (typeof raw === "string" && raw.trim()) {
            const e = raw.trim().toLowerCase();
            return isValidEmail(e) ? [{ value: e, label: e }] : [];
          }
        
          return [];
        });

        setPickupLocations(
          Array.isArray(data.pickupLocations)
            ? data.pickupLocations.map((p) => ({
                showMoreFields: false,
                ...p,
                date: toDateInput(p.date),
              }))
            : []
        );

        setDeliveryLocations(
          Array.isArray(data.deliveryLocations)
            ? data.deliveryLocations.map((p) => ({
                showMoreFields: false,
                ...p,
                date: toDateInput(p.date),
              }))
            : []
        );

        if (data.industryClientId) {
          setClientType("industry");
          setIndustryClientId(data.industryClientId);
        } else if (data.carrierClientId) {
          setClientType("carrier");
          setCarrierClientId(data.carrierClientId);
        }

        // carrierId: backend pode mandar null para "internal"
        if (data.carrierId == null || String(data.carrierId).trim() === "") {
          setCarrierId(INTERNAL_CARRIER_VALUE);
          setCarrierPrefill((prev) => prev || internalCarrierOption);
        } else {
          setCarrierId(String(data.carrierId));
          setCarrierPrefill((prev) =>
            prev || makeOption(data.carrierId, data.carrier?.companyName, t("fallbacks.carrier"))
          );
        }
      } catch (err) {
        console.error("❌ Error fetching data:", err);
      } finally {
        setReady(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [loadId, t]);

  const handlePickupChange = (event, index, field) => {
    const updated = [...pickupLocations];
    const value = event.target.value;

    updated[index][field] = value;

    if (["length", "width", "height"].includes(field)) {
      updated[index][`${field}Error`] = getPointDimensionError(field, value);
    }

    if (showErrors) {
      const current = updated[index];
      if (current) {
        const hiddenErrorKeys = [
          "addressError",
          "postalCodeError",
          "cityError",
          "countryError",
          "gMapsLinkError",
          "coordinatesError",
          "contactPhoneError",
          "lengthError",
          "widthError",
          "heightError",
        ];
        hiddenErrorKeys.forEach((key) => {
          if (current[key] && String(current[key.replace("Error", "")] ?? "").trim()) {
            delete current[key];
          }
        });
      }

      setInvalidFields((prev) => {
        const prevPickups = { ...prev.pickups };
        if (prevPickups[index]) {
          delete prevPickups[index][field];
          if (Object.keys(prevPickups[index]).length === 0) delete prevPickups[index];
        }
        return { ...prev, pickups: prevPickups };
      });
    }

    const allFilled = updated.every(
      (p) =>
        p.location.trim() &&
        !p.lengthError &&
        !p.widthError &&
        !p.heightError
    );

    if (showErrors && allFilled) setPickUpFieldError("");
    setPickupLocations(updated);
  };

  const handleDeliveryChange = (event, index, field) => {
    const updated = [...deliveryLocations];
    const value = event.target.value;

    updated[index][field] = value;

    if (["length", "width", "height"].includes(field)) {
      updated[index][`${field}Error`] = getPointDimensionError(field, value);
    }

    if (showErrors) {
      const current = updated[index];
      if (current) {
        const hiddenFields = [
          "address",
          "postalCode",
          "city",
          "country",
          "gMapsLink",
          "coordinates",
          "contactPhone",
          "length",
          "width",
          "height",
        ];
        hiddenFields.forEach((key) => {
          const errorKey = `${key}Error`;
          if (current[errorKey] && String(current[key] ?? "").trim()) delete current[errorKey];
        });
      }

      setInvalidFields((prev) => {
        const prevDeliveries = { ...prev.deliveries };
        if (prevDeliveries[index]) {
          delete prevDeliveries[index][field];
          if (Object.keys(prevDeliveries[index]).length === 0) delete prevDeliveries[index];
        }
        return { ...prev, deliveries: prevDeliveries };
      });
    }

    const allFilled = updated.every(
      (p) =>
        p.location.trim() &&
        !p.lengthError &&
        !p.widthError &&
        !p.heightError
    );

    if (showErrors && allFilled) setDeliveryFieldError("");
    setDeliveryLocations(updated);
  };

  const validateDeliveryAfterPickups = (deliveries = deliveryLocations, pickups = pickupLocations) => {
    const pickupDateTimes = pickups
      .filter((p) => p.date && p.hour)
      .map((p) => new Date(`${p.date}T${p.hour}`));

    const errorIndexes = { date: [], hour: [] };
    const combined = new Set();

    deliveries.forEach((d, index) => {
      if (d.date && d.hour) {
        const deliveryDateTime = new Date(`${d.date}T${d.hour}`);
        for (const pickupDate of pickupDateTimes) {
          if (deliveryDateTime < pickupDate) {
            const deliveryDateOnly = `${d.date}`;
            const deliveryHour = d.hour;
            const pickupDateOnly = pickupDate.toISOString().split("T")[0];
            const pickupHour = pickupDate.toISOString().split("T")[1].slice(0, 5);

            if (deliveryDateOnly < pickupDateOnly) errorIndexes.date.push(index);
            else if (deliveryDateOnly === pickupDateOnly && deliveryHour < pickupHour) errorIndexes.hour.push(index);

            combined.add(index);
          }
        }
      }
    });

    setDeliveryBeforePickupErrorIndexes(errorIndexes);
    return [...combined];
  };

  useEffect(() => {
    const pickupDateTimes = pickupLocations
      .filter((point) => point.date && point.hour)
      .map((point) => new Date(`${point.date}T${point.hour}`))
      .filter((dateTime) => !Number.isNaN(dateTime.getTime()));

    const errorIndexes = {
      date: [],
      hour: [],
    };

    deliveryLocations.forEach((delivery, index) => {
      if (!delivery.date || !delivery.hour) return;

      const deliveryDateTime = new Date(
        `${delivery.date}T${delivery.hour}`
      );

      if (Number.isNaN(deliveryDateTime.getTime())) return;

      pickupDateTimes.forEach((pickupDateTime) => {
        if (deliveryDateTime >= pickupDateTime) return;

        const pickupDate = pickupDateTime
          .toISOString()
          .slice(0, 10);

        const pickupHour = pickupDateTime
          .toTimeString()
          .slice(0, 5);

        if (delivery.date < pickupDate) {
          errorIndexes.date.push(index);
        } else if (
          delivery.date === pickupDate &&
          delivery.hour < pickupHour
        ) {
          errorIndexes.hour.push(index);
        }
      });
    });

    setDeliveryBeforePickupErrorIndexes({
      date: [...new Set(errorIndexes.date)],
      hour: [...new Set(errorIndexes.hour)],
    });
  }, [pickupLocations, deliveryLocations]);

  const handleSave = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    setNotes4driverError("");
    setNotes4subcontractedError("");
    setPrice4subcontratedError("");
    setPriceError("");
    setClientTypeError("");
    setClientSelectionError("");

    // 🆕 reference
    setReferenceError("");

    // 🆕 reset trailer type errors
    setTrailerTypeError("");
    setTrailerTypeOtherError("");
    setSpecialDimError("");

    // ✅ optional: limit size (matches your DB length 64 if that's what you set)
    if (String(reference || "").length > 64) {
      setReferenceError(t("errors.referenceMax", { defaultValue: "❌ Maximum 64 characters." }));
      return;
    }

    // 🆕 Trailer type is optional, but OTHER still requires a description
    if (
      String(trailerType || "").trim().toUpperCase() === "OTHER" &&
      !String(trailerTypeOther || "").trim()
    ) {
      setTrailerTypeOtherError(
        t("errors.trailerTypeOtherRequired", {
          defaultValue: "❌ Please specify the trailer type.",
        })
      );
      return;
    }

    // 🆕 reset + validate merchandise
    setMerchandiseError("");
    if (!String(merchandise || "").trim()) {
      setMerchandiseError(t("errors.merchandiseRequired"));
      return;
    }
    
    if (notes4driver && notes4driver.length > 2000) {
      setNotes4driverError(t("notes.maxCharsError"));
      return;
    }
    
    if (showNotes4subcontracted && notes4subcontracted && notes4subcontracted.length > 2000) {
      setNotes4subcontractedError(t("notes.maxCharsError"));
      return;
    }    

    const pickupFieldErrors = {};
    pickupLocations.forEach((p, i) => {
      const entry = {};

      if (!p.location?.trim()) {
        entry.location = true;
      }

      if (Object.keys(entry).length) {
        pickupFieldErrors[i] = entry;
      }
    });

    const deliveryFieldErrors = {};
    deliveryLocations.forEach((p, i) => {
      const entry = {};

      if (!p.location?.trim()) {
        entry.location = true;
      }

      if (Object.keys(entry).length) {
        deliveryFieldErrors[i] = entry;
      }
    });

    setInvalidFields({ pickups: pickupFieldErrors, deliveries: deliveryFieldErrors });

    let hasEmptyField = false;
    const invalidPickupIndexes = [];
    const invalidDeliveryIndexesLocal = [];

    const updatedPickups = pickupLocations.map((p, i) => {
      const mainErr = !p.location?.trim();

      const hiddenErrors = {
        addressError: false,
        postalCodeError: false,
        cityError: false,
        countryError: false,
        gMapsLinkError: false,
        coordinatesError: false,
        contactPhoneError: false,

        // 🆕 point-level dimensions are optional
        lengthError:
          String(p.length ?? "").trim() !== ""
            ? getPointDimensionError("length", p.length)
            : "",
        widthError:
          String(p.width ?? "").trim() !== ""
            ? getPointDimensionError("width", p.width)
            : "",
        heightError:
          String(p.height ?? "").trim() !== ""
            ? getPointDimensionError("height", p.height)
            : "",

        loadingAccessError: false,

        // OTHER still requires its description
        handlingEquipmentOtherError:
          String(p.handlingEquipment || "").trim().toUpperCase() === "OTHER" &&
          !String(p.handlingEquipmentOther || "").trim(),
      };
      const anyHiddenErr = Object.values(hiddenErrors).some(Boolean);

      if (mainErr || anyHiddenErr) {
        hasEmptyField = true;
        invalidPickupIndexes.push(i);
      }

      return { ...p, ...hiddenErrors, showMoreFields: mainErr || anyHiddenErr };
    });

    const updatedDeliveries = deliveryLocations.map((p, i) => {
      const mainErr = !p.location?.trim();

      const hiddenErrors = {
        addressError: false,
        postalCodeError: false,
        cityError: false,
        countryError: false,
        gMapsLinkError: false,
        coordinatesError: false,
        contactPhoneError: false,

        // 🆕 point-level dimensions are optional
        lengthError:
          String(p.length ?? "").trim() !== ""
            ? getPointDimensionError("length", p.length)
            : "",
        widthError:
          String(p.width ?? "").trim() !== ""
            ? getPointDimensionError("width", p.width)
            : "",
        heightError:
          String(p.height ?? "").trim() !== ""
            ? getPointDimensionError("height", p.height)
            : "",

        loadingAccessError: false,

        // OTHER still requires its description
        handlingEquipmentOtherError:
          String(p.handlingEquipment || "").trim().toUpperCase() === "OTHER" &&
          !String(p.handlingEquipmentOther || "").trim(),
      };
      const anyHiddenErr = Object.values(hiddenErrors).some(Boolean);

      if (mainErr || anyHiddenErr) {
        hasEmptyField = true;
        invalidDeliveryIndexesLocal.push(i);
      }

      return { ...p, ...hiddenErrors, showMoreFields: mainErr || anyHiddenErr };
    });

    setPickupLocations(updatedPickups);
    setDeliveryLocations(updatedDeliveries);

    setPickUpFieldError(
      updatedPickups.some((point) => !point.location?.trim())
        ? t("errors.locationRequired")
        : ""
    );

    setDeliveryFieldError(
      updatedDeliveries.some((point) => !point.location?.trim())
        ? t("errors.locationRequired")
        : ""
    );

    const invalidPickupFieldsNext = {};
    invalidPickupIndexes.forEach((i) => {
      invalidPickupFieldsNext[i] = { location: true, ...(pickupFieldErrors[i] || {}) };
    });
    const invalidDeliveryFieldsNext = {};
    invalidDeliveryIndexesLocal.forEach((i) => {
      invalidDeliveryFieldsNext[i] = { location: true, ...(deliveryFieldErrors[i] || {}) };
    });

    setInvalidFields({
      pickups: { ...pickupFieldErrors, ...invalidPickupFieldsNext },
      deliveries: { ...deliveryFieldErrors, ...invalidDeliveryFieldsNext },
    });

    if (hasEmptyField) return;

    const invalidIdx = validateDeliveryAfterPickups(updatedDeliveries, updatedPickups);
    if (invalidIdx.length > 0) {
      setFieldValidationError(t("errors.deliveryBeforePickup"));
      return;
    }
    setFieldValidationError("");

        // Dimensions are now calculated from point-level fields
    const lengthVal = Number(pointDimensionSummary.length || 0);
    const heightVal = Number(pointDimensionSummary.height || 0);
    const widthVal = Number(pointDimensionSummary.width || 0);
    const weightVal = parseFloat(weight);

    setLengthError("");
    setHeightError("");
    setWidthError("");

    const toCents = (value) => Math.round(Number(value || 0) * 100);

    const pickupLengthTotal = updatedPickups.reduce((sum, point) => {
      return sum + Number(point.length || 0);
    }, 0);

    const deliveryLengthTotal = updatedDeliveries.reduce((sum, point) => {
      return sum + Number(point.length || 0);
    }, 0);

    const allPickupLengthsProvided =
      updatedPickups.length > 0 &&
      updatedPickups.every(
        (point) => String(point.length ?? "").trim() !== ""
      );

    const allDeliveryLengthsProvided =
      updatedDeliveries.length > 0 &&
      updatedDeliveries.every(
        (point) => String(point.length ?? "").trim() !== ""
      );

    if (
      allPickupLengthsProvided &&
      allDeliveryLengthsProvided &&
      toCents(pickupLengthTotal) !== toCents(deliveryLengthTotal)
    ) {
      const mismatchMessage = t("errors.pickupDeliveryLengthMismatch", {
        defaultValue: "❌ A soma dos comprimentos dos pontos de carga e descarga deve coincidir.",
      });

      setPickUpFieldError(mismatchMessage);
      setDeliveryFieldError(mismatchMessage);
      setFieldValidationError(mismatchMessage);

      setPickupLocations(
        updatedPickups.map((point) => ({
          ...point,
          lengthError: point.lengthError || t("errors.lengthMismatch", {
            defaultValue: "❌ Os totais de comprimento das cargas e descargas devem coincidir.",
          }),
        }))
      );

      setDeliveryLocations(
        updatedDeliveries.map((point) => ({
          ...point,
          lengthError: point.lengthError || t("errors.lengthMismatch", {
            defaultValue: "❌ Os totais de comprimento das cargas e descargas devem coincidir.",
          }),
        }))
      );

      return;
    }

    const hasAnyPointDimension = [
      ...updatedPickups,
      ...updatedDeliveries,
    ].some((point) => {
      return (
        String(point.length ?? "").trim() !== "" ||
        String(point.width ?? "").trim() !== "" ||
        String(point.height ?? "").trim() !== ""
      );
    });

    if (
      hasAnyPointDimension &&
      !pointDimensionSummary.hasSpecialDimension
    ) {
      setSpecialDimError(
        t("errors.specialNeedsOneDimension", {
          defaultValue:
            "⚠️ Esta carga está marcada como especial, mas nenhuma dimensão ultrapassa os limites normais. Aumenta pelo menos uma: comprimento, largura ou altura.",
        })
      );
      return;
    }

    setSpecialDimError("");

    if (String(weight ?? "").trim() !== "") {
      if (Number.isNaN(weightVal) || weightVal <= 0) {
        setWeightError(t("validation.weightPositive"));
        return;
      }
    }

    setWeightError("");

    const priceVal =
      String(price ?? "").trim() === ""
        ? null
        : parseFloat(price);

    if (priceVal === null) {
      setPriceError(
        t("errors.priceRequired", {
          defaultValue: "❌ Price is required.",
        })
      );
      return;
    }

    if (!Number.isFinite(priceVal) || priceVal < 0) {
      setPriceError(t("errors.pricePositive"));
      return;
    }

    setPriceError("");

    const resolvedClientId =
      clientType === "industry"
        ? selectedClient?.value || industryClientId
        : clientType === "carrier"
          ? selectedClient?.value || carrierClientId
          : null;

    if (!clientType) {
      setClientTypeError(
        t("errors.clientTypeRequired", {
          defaultValue: "❌ Client type is required.",
        })
      );
      return;
    }

    if (!resolvedClientId) {
      setClientSelectionError(
        t("errors.clientRequired", {
          defaultValue: "❌ Client is required.",
        })
      );
      return;
    }

    // Subcontracted price is optional
    if (
      showNotes4subcontracted &&
      String(price4subcontrated ?? "").trim() !== ""
    ) {
      const p4sVal = parseFloat(price4subcontrated);

      if (Number.isNaN(p4sVal) || p4sVal < 0) {
        setPrice4subcontratedError(t("errors.pricePositive"));
        return;
      }
    }

    setPrice4subcontratedError("");

    try {
      const resolvedIndustryId = selectedClient?.value || industryClientId || null;
      const resolvedCarrierId = selectedClient?.value || carrierClientId || null;

      setIsSaving(true);

      const cleanNotes4driver =
        typeof notes4driver === "string" ? (notes4driver.trim() || null) : null;

      const cleanNotes4subcontracted =
        showNotes4subcontracted && typeof notes4subcontracted === "string"
          ? (notes4subcontracted.trim() || null)
          : null;      

      await axios.put(`/loads/${loadId}`, {
        length:
          hasAnyPointDimension && Number.isFinite(lengthVal)
            ? lengthVal
            : null,
        height:
          hasAnyPointDimension && Number.isFinite(heightVal)
            ? heightVal
            : null,
        width:
          hasAnyPointDimension && Number.isFinite(widthVal)
            ? widthVal
            : null,
        weight:
          String(weight ?? "").trim() === "" || !Number.isFinite(weightVal)
            ? null
            : weightVal,

        // 🆕 reference (optional)
        reference: String(reference || "").trim() || null,

        // 🆕 merchandise
        merchandise: String(merchandise || "").trim(),

        // 🆕 trailer type (load-level)
        trailerType: String(trailerType || "").trim() || null,
        trailerTypeOther:
          String(trailerType || "").trim().toUpperCase() === "OTHER"
            ? (String(trailerTypeOther || "").trim() || null)
            : null,

        price: priceVal,
        priceCurrency,
        price4subcontrated:
          showNotes4subcontracted &&
          String(price4subcontrated ?? "").trim() !== ""
            ? parseFloat(price4subcontrated)
            : null,

        notes4driver: cleanNotes4driver,
        notes4subcontracted: cleanNotes4subcontracted,
        subcontractedEmails: showNotes4subcontracted
          ? selectedSubcontractedEmails.map((opt) => opt.value).slice(0, 3)
          : [],

        pickupLocations: updatedPickups.map((p) => ({
          location: p.location,
          date: p.date || null,
          hour: p.hour,

          // 🆕 point-level dimensions
          length: p.length === "" ? null : Number(p.length),
          width: p.width === "" ? null : Number(p.width),
          height: p.height === "" ? null : Number(p.height),

          placeId: p.placeId || null,
          address: p.address || "",
          postalCode: p.postalCode || "",
          city: p.city || "",
          country: p.country || "",
          gMapsLink: p.gMapsLink || "",
          coordinates: p.coordinates || "",
          contactPhone: p.contactPhone || "",
          // 🆕 reference (optional)
          reference: String(p.reference || "").trim() || null,
          loadingAccess: p.loadingAccess || null,
          // 🆕 handling equipment
          handlingEquipment: p.handlingEquipment || null,
          handlingEquipmentOther:
            String(p.handlingEquipment || "").trim().toUpperCase() === "OTHER"
              ? (String(p.handlingEquipmentOther || "").trim() || null)
              : null,
        })),

        deliveryLocations: updatedDeliveries.map((p) => ({
          location: p.location,
          date: p.date || null,
          hour: p.hour,

          // 🆕 point-level dimensions
          length: p.length === "" ? null : Number(p.length),
          width: p.width === "" ? null : Number(p.width),
          height: p.height === "" ? null : Number(p.height),

          placeId: p.placeId || null,
          address: p.address || "",
          postalCode: p.postalCode || "",
          city: p.city || "",
          country: p.country || "",
          gMapsLink: p.gMapsLink || "",
          coordinates: p.coordinates || "",
          contactPhone: p.contactPhone || "",
          // 🆕 reference (optional)
          reference: String(p.reference || "").trim() || null,
          loadingAccess: p.loadingAccess || null,
          handlingEquipment: p.handlingEquipment || null,
          handlingEquipmentOther:
            String(p.handlingEquipment || "").trim().toUpperCase() === "OTHER"
              ? (String(p.handlingEquipmentOther || "").trim() || null)
              : null,
        })),

        pickupPoints: updatedPickups.map((p) => ({
          location: p.location,
          date: p.date || null,
          hour: p.hour || null,

          // 🆕 point-level dimensions
          length: p.length === "" ? null : Number(p.length),
          width: p.width === "" ? null : Number(p.width),
          height: p.height === "" ? null : Number(p.height),

          address: p.address || "",
          postalCode: p.postalCode || "",
          city: p.city || "",
          country: p.country || "",
          gMapsLink: p.gMapsLink || "",
          coordinates: p.coordinates || "",
          contactPhone: p.contactPhone || "",
          reference: String(p.reference || "").trim() || null,
          loadingAccess: p.loadingAccess || null,
          handlingEquipment: p.handlingEquipment || null,
          handlingEquipmentOther:
            String(p.handlingEquipment || "").trim().toUpperCase() === "OTHER"
              ? (String(p.handlingEquipmentOther || "").trim() || null)
              : null,
        })),

        deliveryPoints: updatedDeliveries.map((p) => ({
          location: p.location,
          date: p.date || null,
          hour: p.hour || null,

          // 🆕 point-level dimensions
          length: p.length === "" ? null : Number(p.length),
          width: p.width === "" ? null : Number(p.width),
          height: p.height === "" ? null : Number(p.height),

          address: p.address || "",
          postalCode: p.postalCode || "",
          city: p.city || "",
          country: p.country || "",
          gMapsLink: p.gMapsLink || "",
          coordinates: p.coordinates || "",
          contactPhone: p.contactPhone || "",
          reference: String(p.reference || "").trim() || null,
          loadingAccess: p.loadingAccess || null,
          handlingEquipment: p.handlingEquipment || null,
          handlingEquipmentOther:
            String(p.handlingEquipment || "").trim().toUpperCase() === "OTHER"
              ? (String(p.handlingEquipmentOther || "").trim() || null)
              : null,
        })),

        clientType: clientType || null,
        industryClientId:
          clientType === "industry" ? resolvedIndustryId : null,
        carrierClientId:
          clientType === "carrier" ? resolvedCarrierId : null,

        clientContactEmails: selectedClientContactEmails.map((opt) => opt.value),

        carrierId:
          !carrierId || String(carrierId) === INTERNAL_CARRIER_VALUE
            ? null
            : Number(carrierId),
      });

      setIsSaving(false);
      setSuccessPopupOpen(true);
    } catch (error) {
      console.error("Error saving special load:", error);
      setIsSaving(false);
      setFieldValidationError(t("api.updateFailed"));
    }
  };

  // Usa SEMPRE o prefill atual (vindo do state) como “opção fantasma”
  const ghostCarrierOption =
    carrierPrefill ||
    ((prefill?.carrierId || load?.carrierId)
      ? {
          value: Number(prefill?.carrierId ?? load?.carrierId),
          label:
            prefill?.carrierName ||
            prefill?.transportCarrierName ||
            load?.carrierName ||
            load?.carrier?.companyName ||
            t("fallbacks.selectedCarrier"),
        }
      : null);

  // Mixa ghost + carriers carregados (sem duplicar)
  const carrierOptionsWithInitial = useMemo(() => {
    const base = carrierOptionsForSelect;

    if (!ghostCarrierOption) return base;

    const exists = base.some((o) => String(o.value) === String(ghostCarrierOption.value));
    return exists ? base : [ghostCarrierOption, ...base];
  }, [carrierOptionsForSelect, ghostCarrierOption]);

  // Resolve o value mesmo antes das options chegarem (inclui INTERNAL sempre)
  const transportCarrierSelectValue = useMemo(() => {
    const hasCarrier = carrierId != null && String(carrierId).trim() !== "";
    const hasCompany = companyId != null && String(companyId).trim() !== "";

    // se user limpou manualmente
    if (carrierTouched && !hasCarrier) return null;

    // ✅ INTERNAL: sentinel do UI
    if (String(carrierId) === INTERNAL_CARRIER_VALUE) {
      return internalCarrierOption;
    }

    const idNum = hasCarrier ? Number(carrierId) : null;

    if (idNum != null) {
      return (
        carrierOptionsWithInitial.find((o) => String(o.value) === String(idNum)) ||
        ghostCarrierOption || {
          value: idNum,
          label: t("fallbacks.selectedCarrier", { defaultValue: "Selected carrier" }),
        }
      );
    }

    return ghostCarrierOption || null;
  }, [
    carrierId,
    carrierTouched,
    companyId,
    internalCarrierOption,
    carrierOptionsWithInitial,
    ghostCarrierOption,
    t,
  ]);

  if (!ready) return null;

  const handlePriceChange = (e) => {
    const raw = e.target.value;

    if (raw === "") {
      setPrice("");
      setPriceError("");
      return;
    }

    const n = Number(raw);
    if (Number.isNaN(n) || n < 0) {
      setPrice(raw);
      setPriceError(t("errors.pricePositive"));
    } else {
      setPrice(raw);
      setPriceError("");
    }
  };

  const handlePrice4subcontratedChange = (e) => {
    const raw = e.target.value;
  
    if (raw === "") {
      setPrice4subcontrated("");
      setPrice4subcontratedError("");
      return;
    }
  
    const n = Number(raw);
    if (Number.isNaN(n) || n < 0) {
      setPrice4subcontrated(raw);
      setPrice4subcontratedError(t("errors.pricePositive"));
    } else {
      setPrice4subcontrated(raw);
      setPrice4subcontratedError("");
    }
  };
  
  const getClientSelectValue = () => {
    if (!clientType) return null;

    if (clientType === "industry") {
      return (
        industryOptions.find((c) => c.value === Number(industryClientId)) ||
        (industryClientId && prefill?.industryClientName
          ? { value: Number(industryClientId), label: prefill.industryClientName }
          : null)
      );
    }

    return (
      carrierOptions.find((c) => c.value === Number(carrierClientId)) ||
      (carrierClientId && prefill?.carrierClientName
        ? { value: Number(carrierClientId), label: prefill.carrierClientName }
        : null)
    );
  };

  const getPlaceSelectValue = (place, opts) => {
    const norm = (s) => (s || "").trim().toLowerCase();

    if (place.placeId) {
      const inOpts = opts.find((p) => Number(p.value) === Number(place.placeId));
      if (inOpts) return inOpts;
    }

    if (place.location) {
      const byLabel = opts.find((p) => norm(p.label) === norm(place.location));
      if (byLabel) return byLabel;
      return { value: place.location, label: place.location };
    }

    return null;
  };

  return (
    <div className="edit-special-load-container">
      <div className="edit-special-load-back-arrow" onClick={() => navigate("/loads")}>
        ← {t("actions.back")}
      </div>

      {successPopupOpen && (
        <div
          className="edit-special-load-popupOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-special-load-success-title"
        >
          <div className="edit-special-load-popupModal">
            <div className="edit-special-load-popupIconWrap">
              <FaCheckCircle className="edit-special-load-popupIcon" />
            </div>

            <h2
              id="edit-special-load-success-title"
              className="edit-special-load-popupTitle"
            >
              {t("popup.updated_title")}
            </h2>

            <p className="edit-special-load-popupMessage">
              {t("popup.updated_message")}
            </p>

            <div className="edit-special-load-popupActions">
              <button
                type="button"
                className="edit-special-load-popupPrimaryBtn"
                onClick={handleCloseSuccessPopup}
              >
                {t("popup.ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      <h1 className="edit-special-load-title">{t("title")}</h1>

      <div className="edit-special-load-card">
        <form className="edit-special-load-form" onSubmit={handleSave}>

        {/* Top meta + audit */}
          <div className="edit-special-load-topbar">
            <div
              className="edit-special-load-meta"
              title={[
                load?.transportsessionId
                  ? t("meta.transportSessionId", { id: load.transportsessionId })
                  : null,
                (load?.id ?? prefill?.id)
                  ? t("meta.loadId", { id: load?.id ?? prefill?.id })
                  : null,
              ]
                .filter(Boolean)
                .join(" • ")}
            >
              <span className="meta-item">
                <span className="meta-label">{t("meta.transportSession")}:</span>
                <span className="meta-value">{tsCounter != null ? String(tsCounter) : "—"}</span>
              </span>

              <span className="meta-sep">•</span>

              <span className="meta-item">
                <span className="meta-label">{t("meta.load")}:</span>
                <span className="meta-value">{loadCounterVal != null ? String(loadCounterVal) : "—"}</span>
              </span>
            </div>

            <div className="edit-special-load-audit-inline">
              <span
                className="edit-special-load-audit-chip"
                title={displayAuditEmail(load?.createdByEmail ?? prefill?.createdByEmail)}
              >
                <span className="edit-special-load-audit-chip-label">
                  {t("labels.createdByEmail", { defaultValue: "Created by" })}
                </span>
                <span className="edit-special-load-audit-chip-value">
                  {displayAuditEmail(load?.createdByEmail ?? prefill?.createdByEmail)}
                </span>
              </span>

              <span
                className="edit-special-load-audit-chip"
                title={displayAuditEmail(load?.updatedByEmail ?? prefill?.updatedByEmail)}
              >
                <span className="edit-special-load-audit-chip-label">
                  {t("labels.updatedByEmail", { defaultValue: "Updated by" })}
                </span>
                <span className="edit-special-load-audit-chip-value">
                  {displayAuditEmail(load?.updatedByEmail ?? prefill?.updatedByEmail)}
                </span>
              </span>
            </div>
          </div>

          {/* 3 columns */}
          <div className="edit-special-load-columns">
            {/* Column 1 – Pick-Up Points */}
            <div className="load-column">
              <h3 className="column-title">{t("columns.loadingPoints")}</h3>

              {pickupLocations.map((place, index) => (
                <div key={index} className="edit-special-load-multi-location-input">
                  <div className="edit-special-load-label-row">
                    <label className="edit-special-load-label">{t("labels.pickupPlace")}</label>
                    {pickupLocations.length > 1 && (
                      <button
                        type="button"
                        className="edit-special-load-delete-btn"
                        onClick={() => {
                          const updated = [...pickupLocations];
                          updated.splice(index, 1);
                          setPickupLocations(updated);
                        }}
                        title={t("tooltips.removePickup")}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="edit-special-load-input-container vertical">
                    <div className="select-wrapper-left-icon">
                      <CreatableSelect
                        isClearable
                        isMulti={false}
                        options={placeOptions}
                        placeholder={t("place.selectOrType")}
                        value={getPlaceSelectValue(place, placeOptions)}
                        onChange={(selectedOption) => {
                          const updated = [...pickupLocations];

                          if (selectedOption?.__isNew__) {
                            updated[index] = {
                              ...updated[index],
                              placeId: null,
                              location: selectedOption.label,
                              address: "",
                              postalCode: "",
                              city: "",
                              country: "",
                              gMapsLink: "",
                              coordinates: "",
                              contactPhone: "",

                              // 🆕 reference (optional)
                              reference: "",

                              loadingAccess: null,

                              // 🆕 reset handling equipment ao trocar para um local novo
                              handlingEquipment: null,
                              handlingEquipmentOther: "",
                            };
                          } else if (selectedOption?.fullData) {
                            const p = selectedOption.fullData;
                            updated[index] = {
                              ...updated[index],
                              placeId: p.id,
                              location: p.placeName,
                              address: p.address,
                              postalCode: p.postalCode,
                              city: p.city,
                              country: p.country,
                              gMapsLink: p.gMapsLink,
                              coordinates: p.coordinates,
                              contactPhone: p.contactPhone,
                              loadingAccess: p.loadingAccess ?? null,
                            };
                          } else {
                            updated[index] = {
                              ...updated[index],
                              placeId: selectedOption ? Number(selectedOption.value) : null,
                              location: selectedOption?.label || "",
                              address: "",
                              postalCode: "",
                              city: "",
                              country: "",
                              gMapsLink: "",
                              coordinates: "",
                              contactPhone: "",
                              reference: "",
                              loadingAccess: null,
                              // 🆕 reset handling equipment ao trocar para outro local
                              handlingEquipment: null,
                              handlingEquipmentOther: "",
                            };
                          }

                          const cleaned = { ...updated[index] };
                          [
                            "addressError",
                            "postalCodeError",
                            "cityError",
                            "countryError",
                            "gMapsLinkError",
                            "coordinatesError",
                            "contactPhoneError",
                            "loadingAccessError",
                            "handlingEquipmentOtherError",
                          ].forEach((errorKey) => {
                            if (cleaned[errorKey] !== undefined) delete cleaned[errorKey];
                          });
                          updated[index] = cleaned;

                          setPickupLocations(updated);

                          if (updated[index].location.trim()) {
                            setInvalidFields((prev) => {
                              const prevPickups = { ...prev.pickups };
                              if (prevPickups[index]) {
                                delete prevPickups[index].location;
                                if (Object.keys(prevPickups[index]).length === 0) delete prevPickups[index];
                              }
                              return { ...prev, pickups: prevPickups };
                            });
                          }

                          const allFilled = updated.every(
                            (p) =>
                              p.location.trim() &&
                              p.date &&
                              p.hour &&
                              p.address?.trim() &&
                              p.postalCode?.trim() &&
                              p.city?.trim() &&
                              p.country?.trim() &&
                              p.gMapsLink?.trim() &&
                              p.coordinates?.trim() &&
                              p.contactPhone?.trim()
                          );
                          if (allFilled) setPickUpFieldError("");
                        }}
                        styles={customSelectStyles}
                        classNamePrefix={showErrors && invalidFields.pickups[index]?.location ? "select-error" : "select"}
                      />
                    </div>

                    {showErrors && invalidFields.pickups[index]?.location && !place.location.trim() && (
                      <p className="edit-special-load-error-location">{t("errors.locationRequired")}</p>
                    )}

                    <div className="edit-special-load-datetime-group">
                      <div className="custom-input-wrapper">
                        <input
                          type="date"
                          value={place.date}
                          onChange={(e) => handlePickupChange(e, index, "date")}
                          className={`edit-special-load-input ${showErrors && invalidFields.pickups[index]?.date ? "input-error" : ""}`}
                          ref={(el) => {
                            if (el) dateRefs.current[index] = el;
                          }}
                        />
                        <FaCalendarAlt
                          className="edit-special-load-input-icon clickable"
                          onClick={(e) => {
                            e.preventDefault();
                            const input = dateRefs.current[index];
                            if (input) {
                              input.focus();
                              input.showPicker?.();
                            }
                          }}
                        />
                      </div>

                      <div className="custom-input-wrapper">
                        <input
                          type="time"
                          value={place.hour}
                          onChange={(e) => handlePickupChange(e, index, "hour")}
                          className={`edit-special-load-input ${showErrors && invalidFields.pickups[index]?.hour ? "input-error" : ""}`}
                          ref={(el) => {
                            if (el) hourRefs.current[index] = el;
                          }}
                        />
                        <FaClock
                          className="edit-special-load-input-icon clickable"
                          onClick={(e) => {
                            e.preventDefault();
                            const input = hourRefs.current[index];
                            if (input) {
                              input.focus();
                              input.showPicker?.();
                            }
                          }}
                        />
                      </div>
                    </div>

                    <div className="edit-special-load-point-dimensions-block">
                      <label className="edit-special-load-label edit-special-load-point-dimensions-title">
                        {t("labels.pointDimensions", {
                          defaultValue: "Dimensões da carga neste ponto (em metros)",
                        })}*
                      </label>

                      <div className="edit-special-load-point-dimensions-row">
                        <div className="edit-special-load-point-dimension-field">
                          <label>{t("labels.pointLengthShort", { defaultValue: "Comprimento" })}</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={place.length || ""}
                            onChange={(e) => handlePickupChange(e, index, "length")}
                            className={`edit-special-load-input ${place.lengthError ? "input-error" : ""}`}
                            placeholder="0.00m"
                          />
                          {place.lengthError && (
                            <p className="edit-special-load-error">{place.lengthError}</p>
                          )}
                        </div>

                        <div className="edit-special-load-point-dimension-field">
                          <label>{t("labels.pointWidthShort", { defaultValue: "Largura" })}</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={place.width || ""}
                            onChange={(e) => handlePickupChange(e, index, "width")}
                            className={`edit-special-load-input ${place.widthError ? "input-error" : ""}`}
                            placeholder="0.00m"
                          />
                          {place.widthError && (
                            <p className="edit-special-load-error">{place.widthError}</p>
                          )}
                        </div>

                        <div className="edit-special-load-point-dimension-field">
                          <label>{t("labels.pointHeightShort", { defaultValue: "Altura" })}</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={place.height || ""}
                            onChange={(e) => handlePickupChange(e, index, "height")}
                            className={`edit-special-load-input ${place.heightError ? "input-error" : ""}`}
                            placeholder="0.00m"
                          />
                          {place.heightError && (
                            <p className="edit-special-load-error">{place.heightError}</p>
                          )}
                        </div>
                      </div>
                    </div>

                     {/* 🆕 Loading Access */}
                    <label className="edit-special-load-label">
                      {t("loadingAccess.label", { defaultValue: "Loading access" })}
                    </label>

                    <div className="edit-special-load-select-wrapper" style={{ marginTop: -10, marginBottom: 5 }}>
                      <Select
                        options={loadingAccessOptions}
                        value={
                          loadingAccessOptions.find(
                            (o) => o.value === String(place.loadingAccess || "").trim().toUpperCase()
                          ) || null
                        }
                        onChange={(opt) => handleLoadingAccessChange(index, opt, false)}
                        isClearable
                        placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                        styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        classNamePrefix={place.loadingAccessError ? "select-error" : "select"}
                        menuPortalTarget={document.body}
                      />
                    </div>

                    {/* 🆕 Handling Equipment */}
                    <label className="edit-special-load-label">
                      {t("handlingEquipment.label", { defaultValue: "Handling equipment" })}
                    </label>

                    <div className="edit-special-load-select-wrapper" style={{ marginTop: -10, marginBottom: 10 }}>
                      <Select
                        options={handlingEquipmentOptions}
                        value={
                          handlingEquipmentOptions.find(
                            (o) => o.value === String(place.handlingEquipment || "").trim().toUpperCase()
                          ) || null
                        }
                        onChange={(opt) => handleHandlingEquipmentChange(index, opt, false)}
                        isClearable
                        placeholder={t("fields.handlingEquipment.placeholder", { defaultValue: "Select equipment..." })}
                        styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        classNamePrefix={place.handlingEquipmentOtherError ? "select-error" : "select"}
                        menuPortalTarget={document.body}
                      />
                    </div>

                    {String(place.handlingEquipment || "").trim().toUpperCase() === "OTHER" && (
                      <div className="edit-special-load-field-group" style={{ marginTop: -10, marginBottom: 10 }}>
                        <label className="edit-special-load-label">
                          {t("fields.handlingEquipmentOther", { defaultValue: "Other (specify)" })}
                        </label>

                        <input
                          type="text"
                          placeholder={t("fields.handlingEquipmentOtherPlaceholder", { defaultValue: "Write the equipment..." })}
                          value={place.handlingEquipmentOther || ""}
                          onChange={(e) => {
                            const v = e.target.value ?? "";
                            setPickupLocations((prev) => {
                              const updated = [...prev];
                              if (!updated[index]) return prev;
                              updated[index] = {
                                ...updated[index],
                                handlingEquipmentOther: v,
                                handlingEquipmentOtherError: false,
                              };
                              return updated;
                            });
                          }}
                          className={`edit-special-load-input ${place.handlingEquipmentOtherError ? "input-error" : ""}`}
                        />
                      </div>
                    )}

                    {/* 🆕 Reference (optional) */}
                    <div className="edit-special-load-field-group" style={{ marginTop: -6, marginBottom: 10 }}>
                      <label className="edit-special-load-label">
                        {t("fields.reference", { defaultValue: "Reference" })}
                      </label>

                      <input
                        type="text"
                        value={place.reference || ""}
                        onChange={(e) => handlePickupChange(e, index, "reference")}
                        placeholder={t("placeholders.reference", { defaultValue: "e.g. PO 12345, customer ref..." })}
                        className="edit-special-load-input"
                        maxLength={64}
                      />
                    </div>

                    {place.showMoreFields ? (
                      <>
                        <input
                          type="text"
                          placeholder={t("place.address")}
                          value={place.address || ""}
                          onChange={(e) => handlePickupChange(e, index, "address")}
                          className={`edit-special-load-input ${place.addressError ? "input-error" : ""}`}
                        />

                        <div className="edit-special-load-row-group">
                          <input
                            type="text"
                            placeholder={t("place.postalCode")}
                            value={place.postalCode || ""}
                            onChange={(e) => handlePickupChange(e, index, "postalCode")}
                            className={`edit-special-load-input ${place.postalCodeError ? "input-error" : ""}`}
                          />
                          <input
                            type="text"
                            placeholder={t("place.city")}
                            value={place.city || ""}
                            onChange={(e) => handlePickupChange(e, index, "city")}
                            className={`edit-special-load-input ${place.cityError ? "input-error" : ""}`}
                          />
                          <input
                            type="text"
                            placeholder={t("place.country")}
                            value={place.country || ""}
                            onChange={(e) => handlePickupChange(e, index, "country")}
                            className={`edit-special-load-input ${place.countryError ? "input-error" : ""}`}
                          />
                        </div>

                        <input
                          type="text"
                          placeholder={t("place.contactPhone")}
                          value={place.contactPhone || ""}
                          onChange={(e) => handlePickupChange(e, index, "contactPhone")}
                          className={`edit-special-load-input ${place.contactPhoneError ? "input-error" : ""}`}
                        />
                        <input
                          type="text"
                          placeholder={t("place.gMapsLink")}
                          value={place.gMapsLink || ""}
                          onChange={(e) => handlePickupChange(e, index, "gMapsLink")}
                          className={`edit-special-load-input ${place.gMapsLinkError ? "input-error" : ""}`}
                        />
                        <input
                          type="text"
                          placeholder={t("place.coordinates")}
                          value={place.coordinates || ""}
                          onChange={(e) => handlePickupChange(e, index, "coordinates")}
                          className={`edit-special-load-input ${place.coordinatesError ? "input-error" : ""}`}
                        />

                        <div className="edit-special-load-toggle-fields-wrapper">
                          <button
                            type="button"
                            className="edit-special-load-toggle-fields-btn"
                            onClick={() => {
                              setPickupLocations((prev) => {
                                const updated = [...prev];
                                if (updated[index]) updated[index].showMoreFields = false;
                                return updated;
                              });
                            }}
                          >
                            {t("place.hideFields")} <FiChevronUp className="edit-special-load-toggle-arrow" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="edit-special-load-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="edit-special-load-toggle-fields-btn"
                          onClick={() => {
                            setPickupLocations((prev) => {
                              const updated = [...prev];
                              if (updated[index]) updated[index].showMoreFields = true;
                              return updated;
                            });
                          }}
                        >
                          {t("place.moreFields")} <FiChevronDown className="edit-special-load-toggle-arrow" />
                        </button>
                      </div>
                    )}

                    {pickUpFieldError && <p className="edit-special-load-error pickup-error">{pickUpFieldError}</p>}
                  </div>
                </div>
              ))}

              {pickupLocations.length < 7 ? (
                <button
                  type="button"
                  className="add-location-btn"
                  onClick={() =>
                    setPickupLocations([
                      ...pickupLocations,
                      {
                        location: "",
                        date: "",
                        hour: "",

                        // 🆕 point-level dimensions
                        length: "",
                        width: "",
                        height: "",
                        lengthError: "",
                        widthError: "",
                        heightError: "",

                        placeId: null,
                        address: "",
                        postalCode: "",
                        city: "",
                        country: "",
                        gMapsLink: "",
                        coordinates: "",
                        contactPhone: "",

                        // 🆕 reference (optional)
                        reference: "",

                        loadingAccess: null, // ✅ add

                        // 🆕 add
                        handlingEquipment: null,
                        handlingEquipmentOther: "",

                        showMoreFields: false,
                      },
                    ])
                  }
                >
                  {t("buttons.addPickup")}
                </button>
              ) : (
                <p className="edit-special-load-error-btn pickup-error">{t("errors.maxPickups")}</p>
              )}
            </div>

            {/* Column 2 – Dimensions + Client */}
            <div className="load-column">
              <h3 className="column-title">{t("columns.merchandise")}</h3>

              <div className="edit-special-load-merchandise-inline-row">
                <div className="edit-special-load-field-group edit-special-load-weight-field">
                  <label className="edit-special-load-label">{t("labels.weight")} (kg)</label>
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => {
                      const val = e.target.value;
                      setWeight(val);

                      const num = parseFloat(val);

                      if (!val.trim()) {
                        setWeightError("");
                      } else if (Number.isNaN(num) || num <= 0) {
                        setWeightError(t("validation.weightPositive"));
                      } else {
                        setWeightError("");
                      }
                    }}
                    className={`edit-special-load-input ${weightError ? "input-error" : ""}`}
                    placeholder="0"
                  />
                  {weightError && <p className="edit-special-load-error">{weightError}</p>}
                </div>

                <div className="edit-special-load-field-group edit-special-load-merchandise-field">
                  <label className="edit-special-load-label">{t("labels.merchandise")}</label>
                  <input
                    type="text"
                    value={merchandise}
                    onChange={(e) => {
                      const v = e.target.value;
                      setMerchandise(v);
                      if (merchandiseError && v.trim()) setMerchandiseError("");
                    }}
                    placeholder={t("placeholders.merchandise")}
                    className={`edit-special-load-input edit-special-load-merchandise-input ${merchandiseError ? "input-error" : ""}`}
                  />
                  {merchandiseError && <p className="edit-special-load-error">{merchandiseError}</p>}
                </div>
              </div>

              <div className="edit-special-load-load-dimensions-summary">
                <label className="edit-special-load-label edit-special-load-load-dimensions-title">
                  {t("labels.loadDimensions", {
                    defaultValue: "Dimensões totais da carga",
                  })}
                </label>

                <div className="edit-special-load-load-dimensions-row">
                  <div className="edit-special-load-load-dimension-field">
                    <label>{t("labels.length", { defaultValue: "Comprimento" })} (m)</label>
                    <input
                      type="text"
                      value={length}
                      readOnly
                      className="edit-special-load-input edit-special-load-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="edit-special-load-load-dimension-field">
                    <label>{t("labels.width", { defaultValue: "Largura" })} (m)</label>
                    <input
                      type="text"
                      value={width}
                      readOnly
                      className="edit-special-load-input edit-special-load-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="edit-special-load-load-dimension-field">
                    <label>{t("labels.height", { defaultValue: "Altura" })} (m)</label>
                    <input
                      type="text"
                      value={height}
                      readOnly
                      className="edit-special-load-input edit-special-load-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {!pointDimensionSummary.lengthMatches && (
                  <p className="edit-special-load-dimension-warning-text">
                    {t("errors.pickupDeliveryLengthMismatch", {
                      defaultValue: "⚠️ A soma dos comprimentos dos pontos de carga e descarga ainda não coincide.",
                    })}
                  </p>
                )}

                {specialDimError && (
                  <p className="edit-special-load-error">{specialDimError}</p>
                )}

                <div className="edit-special-load-dimension-helper-text">
                  {t("labels.loadDimensionsHelper", {
                    defaultValue:
                      "Comprimento calculado pela soma dos pontos. Largura e altura usam o maior valor registado.",
                  })}
                </div>
              </div>

              {/* 🆕 Trailer Type (load-level) */}
              <div className="edit-special-load-field-group">
                <label className="edit-special-load-label">
                  {t("fields.trailerType", { defaultValue: "Trailer type" })}
                </label>

                <Select
                  options={trailerTypeOptions}
                  value={
                    trailerTypeOptions.find(
                      (o) => o.value === String(trailerType || "").trim().toUpperCase()
                    ) || null
                  }
                  onChange={handleTrailerTypeChange}
                  isClearable
                  isSearchable={false}
                  placeholder={t("placeholders.trailerType", { defaultValue: "Select trailer type..." })}
                  styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  classNamePrefix={trailerTypeError ? "select-error" : "select"}
                  menuPortalTarget={document.body}
                />

                {trailerTypeError && <p className="edit-special-load-error-client">{trailerTypeError}</p>}
              </div>

              {String(trailerType || "").trim().toUpperCase() === "OTHER" && (
                <div className="edit-special-load-field-group">
                  <label className="edit-special-load-label">
                    {t("fields.trailerTypeOther", { defaultValue: "Other (specify)" })}
                  </label>

                  <input
                    type="text"
                    value={trailerTypeOther || ""}
                    onChange={handleTrailerTypeOtherChange}
                    placeholder={t("placeholders.trailerTypeOther", { defaultValue: "Write the trailer type..." })}
                    className={`edit-special-load-input ${trailerTypeOtherError ? "input-error" : ""}`}
                  />

                  {trailerTypeOtherError && <p className="edit-special-load-error">{trailerTypeOtherError}</p>}
                </div>
              )}

              <h3 className="general-info-title">{t("sections.generalInfo")}</h3>

              {/* 💰 Price */}
              <div className="edit-special-load-field-group">
                <label className="edit-special-load-label">{t("labels.price")}</label>

                <div className={`edit-special-load-price-wrapper ${priceError ? "input-error" : ""}`}>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={price ?? ""}
                    onChange={handlePriceChange}
                    className="edit-special-load-input edit-special-load-price-input"
                  />

                  <div className="edit-special-load-price-currency">
                    <Select
                      options={CURRENCY_OPTIONS}
                      value={CURRENCY_OPTIONS.find((o) => o.value === (priceCurrency || "EUR")) || CURRENCY_OPTIONS[0]}
                      onChange={(opt) => setPriceCurrency(opt?.value || "EUR")}
                      isClearable={false}
                      isSearchable={false}
                      styles={customSelectStyles}
                      classNamePrefix="edit-special-load-select"
                    />
                  </div>
                </div>

                {priceError && <p className="edit-special-load-error">{priceError}</p>}
              </div>

              {/* 🆕 Price for Subcontracted (ONLY when external carrier) */}
              {showNotes4subcontracted && (
                <div className="edit-special-load-field-group">
                  <label className="edit-special-load-label">{t("labels.price4subcontrated")}</label>

                  <div className={`edit-special-load-price-wrapper ${price4subcontratedError ? "input-error" : ""}`}>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={price4subcontrated ?? ""}
                      onChange={handlePrice4subcontratedChange}
                      className="edit-special-load-input edit-special-load-price-input"
                    />

                    <div className="edit-special-load-price-currency">
                      <Select
                        options={CURRENCY_OPTIONS}
                        value={CURRENCY_OPTIONS.find((o) => o.value === (priceCurrency || "EUR")) || CURRENCY_OPTIONS[0]}
                        onChange={(opt) => setPriceCurrency(opt?.value || "EUR")}
                        isClearable={false}
                        isSearchable={false}
                        styles={customSelectStyles}
                        classNamePrefix="edit-special-load-select"
                      />
                    </div>
                  </div>

                  {price4subcontratedError && <p className="edit-special-load-error">{price4subcontratedError}</p>}
                </div>
              )}

              {/* Client Type */}
              <div className="edit-special-load-field-group">
                <label className="edit-special-load-label">{t("labels.clientType")}</label>
                <Select
                  options={[
                    { label: t("clientTypes.industry"), value: "industry" },
                    { label: t("clientTypes.carrier"), value: "carrier" },
                  ]}
                  value={
                    clientType
                      ? {
                          label: clientType === "industry" ? t("clientTypes.industry") : t("clientTypes.carrier"),
                          value: clientType,
                        }
                      : null
                  }
                  onChange={(opt) => {
                    const selected = opt ? opt.value : "";
                    setClientType(selected);
                    setClientTypeError("");
                    setClientSelectionError("");
                    setIndustryClientId("");
                    setCarrierClientId("");
                    setSelectedClient(null);
                    setSelectedClientContactEmails([]);
                  }}
                  isClearable
                  isSearchable={false}
                  styles={customSelectStyles}
                  classNamePrefix={clientTypeError ? "select-error" : "select"}
                />

                {clientTypeError && (
                  <p className="edit-special-load-error">
                    {clientTypeError}
                  </p>
                )}
              </div>

              {/* Client selection */}
              {clientType && (
                <div className="edit-special-load-field-group">
                  <label className="edit-special-load-label">
                    {clientType === "industry" ? t("labels.industryClient") : t("labels.carrierClient")}
                  </label>
                  <Select
                    options={clientType === "industry" ? industryOptions : carrierOptions}
                    value={getClientSelectValue()}
                    onChange={(opt) => {
                      setSelectedClient(opt || null);
                      setClientSelectionError("");
                      setSelectedClientContactEmails([]);

                      if (clientType === "industry") {
                        setIndustryClientId(opt ? opt.value : "");
                      } else {
                        setCarrierClientId(opt ? opt.value : "");
                      }
                    }}
                    isClearable
                    isSearchable
                    styles={customSelectStyles}
                    classNamePrefix={clientSelectionError ? "select-error" : "select"}
                  />

                  {clientSelectionError && (
                    <p className="edit-special-load-error">
                      {clientSelectionError}
                    </p>
                  )}

                  {((clientType === "industry" && industryClientId) || (clientType === "carrier" && carrierClientId)) && (
                    <div className="edit-special-load-field-group">
                      <label className="edit-special-load-label">
                        {t("fields.clientContactEmails.label", {
                          defaultValue: "Client contact email(s)",
                        })}
                      </label>

                      <Select
                        isMulti
                        isClearable
                        closeMenuOnSelect={false}
                        options={selectedClientContactEmailOptions}
                        value={selectedClientContactEmails}
                        onChange={(selectedOptions) => {
                          const limited = Array.isArray(selectedOptions)
                            ? selectedOptions.slice(0, 3)
                            : [];
                          setSelectedClientContactEmails(limited);
                        }}
                        placeholder={t("fields.clientContactEmails.placeholder", {
                          defaultValue: "Select up to 3 contact email(s)...",
                        })}
                        noOptionsMessage={() =>
                          t("fields.clientContactEmails.noOptions", {
                            defaultValue: "No contact emails found for this client.",
                          })
                        }
                        styles={multiSelectHorizontalStyles}
                        classNamePrefix="select"
                        menuPortalTarget={document.body}
                      />

                      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                        {t("fields.clientContactEmails.helper", {
                          defaultValue: "These emails come from the client's contact list.",
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Transport Carrier */}
              <div className="edit-special-load-field-group">
                <label className="edit-special-load-label">{t("labels.transportCarrier")}</label>
                <Select
                  options={carrierOptionsWithInitial}
                  value={transportCarrierSelectValue}
                  onChange={(opt) => {
                    setCarrierTouched(true);
                    setCarrierId(opt ? String(opt.value) : "");
                    setSelectedSubcontractedEmails([]);
                  }}
                  isClearable
                  isSearchable
                  placeholder={t("client.selectTransportCarrier")}
                  styles={customSelectStyles}
                  classNamePrefix="select"
                />
              </div>

              {showNotes4subcontracted && (
                <div className="edit-special-load-field-group">
                  <label className="edit-special-load-label">
                    {t("fields.subcontractedEmail.label", {
                      defaultValue: "Subcontracted Emails",
                    })}
                  </label>

                  <Select
                    isMulti
                    isClearable
                    closeMenuOnSelect={false}
                    options={selectedSubcontractedEmailOptions}
                    value={selectedSubcontractedEmails}
                    onChange={(selectedOptions) => {
                      const limited = Array.isArray(selectedOptions)
                        ? selectedOptions.slice(0, 3)
                        : [];
                      setSelectedSubcontractedEmails(limited);
                    }}
                    placeholder={t("fields.subcontractedEmail.placeholder", {
                      defaultValue: "Select up to 3 subcontracted emails...",
                    })}
                    noOptionsMessage={() =>
                      t("fields.subcontractedEmail.noOptions", {
                        defaultValue: "No contact emails found for this subcontracted carrier.",
                      })
                    }
                    styles={multiSelectHorizontalStyles}
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                  />

                  <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                    {t("fields.subcontractedEmail.helper", {
                      defaultValue:
                        "These emails come from the subcontracted carrier contact list. Maximum: 3.",
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Column 3 – Delivery Points */}
            <div className="load-column">
              <h3 className="column-title">{t("columns.unloadingPoints")}</h3>

              {deliveryLocations.map((place, index) => (
                <div key={index} className="edit-special-load-multi-location-input">
                  <div className="edit-special-load-label-row">
                    <label className="edit-special-load-label">{t("labels.deliveryPlace")}</label>
                    {deliveryLocations.length > 1 && (
                      <button
                        type="button"
                        className="edit-special-load-delete-btn"
                        onClick={() => {
                          const updated = [...deliveryLocations];
                          updated.splice(index, 1);
                          setDeliveryLocations(updated);
                        }}
                        title={t("tooltips.removeDelivery")}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="edit-special-load-input-container vertical">
                    <div className="select-wrapper-left-icon">
                      <CreatableSelect
                        isClearable
                        isMulti={false}
                        options={placeOptions}
                        placeholder={t("place.selectOrType")}
                        value={getPlaceSelectValue(place, placeOptions)}
                        onChange={(selectedOption) => {
                          const updated = [...deliveryLocations];

                          if (selectedOption?.__isNew__) {
                            updated[index] = {
                              ...updated[index],
                              placeId: null,
                              location: selectedOption.label,
                              address: "",
                              postalCode: "",
                              city: "",
                              country: "",
                              gMapsLink: "",
                              coordinates: "",
                              contactPhone: "",

                              // 🆕 reference (optional)
                              reference: "",

                              loadingAccess: null,

                              // 🆕 reset handling equipment ao trocar para um local novo
                              handlingEquipment: null,
                              handlingEquipmentOther: "",
                            };
                          } else if (selectedOption?.fullData) {
                            const p = selectedOption.fullData;
                            updated[index] = {
                              ...updated[index],
                              placeId: p.id,
                              location: p.placeName,
                              address: p.address,
                              postalCode: p.postalCode,
                              city: p.city,
                              country: p.country,
                              gMapsLink: p.gMapsLink,
                              coordinates: p.coordinates,
                              contactPhone: p.contactPhone,
                              reference: updated[index]?.reference || "",
                              loadingAccess: p.loadingAccess ?? null,
                              // 🆕 NEW: handling equipment (per point)
                              handlingEquipment: p.handlingEquipment ?? null,
                              handlingEquipmentOther: p.handlingEquipmentOther ?? "",
                            };
                          } else {
                            updated[index] = {
                              ...updated[index],
                              placeId: selectedOption ? Number(selectedOption.value) : null,
                              location: selectedOption?.label || "",
                              address: "",
                              postalCode: "",
                              city: "",
                              country: "",
                              gMapsLink: "",
                              coordinates: "",
                              contactPhone: "",
                              reference: "",
                              loadingAccess: null,
                              // 🆕 reset handling equipment ao trocar para outro local
                              handlingEquipment: null,
                              handlingEquipmentOther: "",
                            };
                          }

                          const cleaned = { ...updated[index] };
                          [
                            "addressError",
                            "postalCodeError",
                            "cityError",
                            "countryError",
                            "gMapsLinkError",
                            "coordinatesError",
                            "contactPhoneError",
                            "loadingAccessError",
                            "handlingEquipmentOtherError",
                          ].forEach((errorKey) => {
                            if (cleaned[errorKey] !== undefined) delete cleaned[errorKey];
                          });
                          updated[index] = cleaned;

                          setDeliveryLocations(updated);

                          const allFilled = updated.every(
                            (p) =>
                              p.location.trim() &&
                              p.date &&
                              p.hour &&
                              p.address?.trim() &&
                              p.postalCode?.trim() &&
                              p.city?.trim() &&
                              p.country?.trim() &&
                              p.gMapsLink?.trim() &&
                              p.coordinates?.trim() &&
                              p.contactPhone?.trim()
                          );
                          if (allFilled) setDeliveryFieldError("");

                          setInvalidFields((prev) => {
                            const prevDeliveries = { ...prev.deliveries };
                            if (prevDeliveries[index]) {
                              delete prevDeliveries[index].location;
                              if (Object.keys(prevDeliveries[index]).length === 0) delete prevDeliveries[index];
                            }
                            return { ...prev, deliveries: prevDeliveries };
                          });
                        }}
                        styles={customSelectStyles}
                        classNamePrefix={showErrors && invalidFields.deliveries[index]?.location ? "select-error" : "select"}
                      />
                    </div>

                    {showErrors && invalidFields.deliveries[index]?.location && !place.location.trim() && (
                      <p className="edit-special-load-error-location">{t("errors.locationRequired")}</p>
                    )}

                    <div className="edit-special-load-datetime-group">
                      <div className="custom-input-wrapper">
                        <input
                          type="date"
                          value={place.date}
                          onChange={(e) => handleDeliveryChange(e, index, "date")}
                          className={`edit-special-load-input ${
                            showErrors &&
                            (invalidFields.deliveries[index]?.date || deliveryBeforePickupErrorIndexes.date.includes(index))
                              ? "input-error"
                              : ""
                          }`}
                          ref={(el) => (deliveryDateRefs.current[index] = el)}
                        />
                        <FaCalendarAlt
                          className="edit-special-load-input-icon clickable"
                          onClick={(e) => {
                            e.preventDefault();
                            const input = deliveryDateRefs.current[index];
                            if (input) {
                              input.focus();
                              input.showPicker?.();
                            }
                          }}
                        />
                      </div>

                      <div className="custom-input-wrapper">
                        <input
                          type="time"
                          value={place.hour}
                          onChange={(e) => handleDeliveryChange(e, index, "hour")}
                          className={`edit-special-load-input ${
                            showErrors &&
                            (invalidFields.deliveries[index]?.hour || deliveryBeforePickupErrorIndexes.hour.includes(index))
                              ? "input-error"
                              : ""
                          }`}
                          ref={(el) => (deliveryHourRefs.current[index] = el)}
                        />
                        <FaClock
                          className="edit-special-load-input-icon clickable"
                          onClick={(e) => {
                            e.preventDefault();
                            const input = deliveryHourRefs.current[index];
                            if (input) {
                              input.focus();
                              input.showPicker?.();
                            }
                          }}
                        />
                      </div>
                    </div>

                    <div className="edit-special-load-point-dimensions-block">
                      <label className="edit-special-load-label edit-special-load-point-dimensions-title">
                        {t("labels.pointDimensions", {
                          defaultValue: "Dimensões da carga neste ponto (em metros)",
                        })}
                      </label>

                      <div className="edit-special-load-point-dimensions-row">
                        <div className="edit-special-load-point-dimension-field">
                          <label>{t("labels.pointLengthShort", { defaultValue: "Comprimento" })}</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={place.length || ""}
                            onChange={(e) => handleDeliveryChange(e, index, "length")}
                            className={`edit-special-load-input ${place.lengthError ? "input-error" : ""}`}
                            placeholder="0.00m"
                          />
                          {place.lengthError && (
                            <p className="edit-special-load-error">{place.lengthError}</p>
                          )}
                        </div>

                        <div className="edit-special-load-point-dimension-field">
                          <label>{t("labels.pointWidthShort", { defaultValue: "Largura" })}</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={place.width || ""}
                            onChange={(e) => handleDeliveryChange(e, index, "width")}
                            className={`edit-special-load-input ${place.widthError ? "input-error" : ""}`}
                            placeholder="0.00m"
                          />
                          {place.widthError && (
                            <p className="edit-special-load-error">{place.widthError}</p>
                          )}
                        </div>

                        <div className="edit-special-load-point-dimension-field">
                          <label>{t("labels.pointHeightShort", { defaultValue: "Altura" })}</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={place.height || ""}
                            onChange={(e) => handleDeliveryChange(e, index, "height")}
                            className={`edit-special-load-input ${place.heightError ? "input-error" : ""}`}
                            placeholder="0.00m"
                          />
                          {place.heightError && (
                            <p className="edit-special-load-error">{place.heightError}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 🆕 Loading Access */}
                    <label className="edit-special-load-label">
                      {t("loadingAccess.label", { defaultValue: "Loading access" })}
                    </label>

                    <div className="edit-special-load-select-wrapper" style={{marginTop: -10, marginBottom: 5 }}>
                      <Select
                        options={loadingAccessOptions}
                        value={
                          loadingAccessOptions.find(
                            (o) => o.value === String(place.loadingAccess || "").trim().toUpperCase()
                          ) || null
                        }
                        onChange={(opt) => handleLoadingAccessChange(index, opt, true)}
                        isClearable
                        placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                        styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        classNamePrefix={place.loadingAccessError ? "select-error" : "select"}
                        menuPortalTarget={document.body}
                      />
                    </div>

                    {/* 🆕 Handling Equipment */}
                    <label className="edit-special-load-label">
                      {t("handlingEquipment.label", { defaultValue: "Handling equipment" })}
                    </label>

                    <div className="edit-special-load-select-wrapper" style={{ marginTop: -10, marginBottom: 10 }}>
                      <Select
                        options={handlingEquipmentOptions}
                        value={
                          handlingEquipmentOptions.find(
                            (o) => o.value === String(place.handlingEquipment || "").trim().toUpperCase()
                          ) || null
                        }
                        onChange={(opt) => handleHandlingEquipmentChange(index, opt, true)}
                        isClearable
                        placeholder={t("fields.handlingEquipment.placeholder", { defaultValue: "Select equipment..." })}
                        styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        classNamePrefix={place.handlingEquipmentOtherError ? "select-error" : "select"}
                        menuPortalTarget={document.body}
                      />
                    </div>

                    {String(place.handlingEquipment || "").trim().toUpperCase() === "OTHER" && (
                      <div className="edit-special-load-field-group" style={{ marginTop: -10, marginBottom: 10 }}>
                        <label className="edit-special-load-label">
                          {t("fields.handlingEquipmentOther", { defaultValue: "Other (specify)" })}
                        </label>

                        <input
                          type="text"
                          placeholder={t("fields.handlingEquipmentOtherPlaceholder", { defaultValue: "Write the equipment..." })}
                          value={place.handlingEquipmentOther || ""}
                          onChange={(e) => {
                            const v = e.target.value ?? "";
                            setDeliveryLocations((prev) => {
                              const updated = [...prev];
                              if (!updated[index]) return prev;
                              updated[index] = {
                                ...updated[index],
                                handlingEquipmentOther: v,
                                handlingEquipmentOtherError: false,
                              };
                              return updated;
                            });
                          }}
                          className={`edit-special-load-input ${place.handlingEquipmentOtherError ? "input-error" : ""}`}
                        />
                      </div>
                    )}

                    {/* 🆕 Reference (optional) */}
                    <div className="edit-special-load-field-group" style={{ marginTop: -6, marginBottom: 10 }}>
                      <label className="edit-special-load-label">
                        {t("fields.reference", { defaultValue: "Reference" })}
                      </label>

                      <input
                        type="text"
                        value={place.reference || ""}
                        onChange={(e) => handleDeliveryChange(e, index, "reference")}
                        placeholder={t("placeholders.reference", { defaultValue: "e.g. PO 12345, customer ref..." })}
                        className="edit-special-load-input"
                        maxLength={64}
                      />
                    </div>

                    {place.showMoreFields ? (
                      <>
                        <input
                          type="text"
                          placeholder={t("place.address")}
                          value={place.address || ""}
                          onChange={(e) => handleDeliveryChange(e, index, "address")}
                          className={`edit-special-load-input ${showErrors && place.addressError ? "input-error" : ""}`}
                        />

                        <div className="edit-special-load-row-group">
                          <input
                            type="text"
                            placeholder={t("place.postalCode")}
                            value={place.postalCode || ""}
                            onChange={(e) => handleDeliveryChange(e, index, "postalCode")}
                            className={`edit-special-load-input ${showErrors && place.postalCodeError ? "input-error" : ""}`}
                          />
                          <input
                            type="text"
                            placeholder={t("place.city")}
                            value={place.city || ""}
                            onChange={(e) => handleDeliveryChange(e, index, "city")}
                            className={`edit-special-load-input ${showErrors && place.cityError ? "input-error" : ""}`}
                          />
                          <input
                            type="text"
                            placeholder={t("place.country")}
                            value={place.country || ""}
                            onChange={(e) => handleDeliveryChange(e, index, "country")}
                            className={`edit-special-load-input ${showErrors && place.countryError ? "input-error" : ""}`}
                          />
                        </div>

                        <input
                          type="text"
                          placeholder={t("place.contactPhone")}
                          value={place.contactPhone || ""}
                          onChange={(e) => handleDeliveryChange(e, index, "contactPhone")}
                          className={`edit-special-load-input ${showErrors && place.contactPhoneError ? "input-error" : ""}`}
                        />
                        <input
                          type="text"
                          placeholder={t("place.gMapsLink")}
                          value={place.gMapsLink || ""}
                          onChange={(e) => handleDeliveryChange(e, index, "gMapsLink")}
                          className={`edit-special-load-input ${showErrors && place.gMapsLinkError ? "input-error" : ""}`}
                        />
                        <input
                          type="text"
                          placeholder={t("place.coordinates")}
                          value={place.coordinates || ""}
                          onChange={(e) => handleDeliveryChange(e, index, "coordinates")}
                          className={`edit-special-load-input ${showErrors && place.coordinatesError ? "input-error" : ""}`}
                        />

                        <div className="edit-special-load-toggle-fields-wrapper">
                          <button
                            type="button"
                            className="edit-special-load-toggle-fields-btn"
                            onClick={() => {
                              setDeliveryLocations((prev) => {
                                const updated = [...prev];
                                if (updated[index]) updated[index].showMoreFields = false;
                                return updated;
                              });
                            }}
                          >
                            {t("place.hideFields")} <FiChevronUp className="edit-special-load-toggle-arrow" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="edit-special-load-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="edit-special-load-toggle-fields-btn"
                          onClick={() => {
                            setDeliveryLocations((prev) => {
                              const updated = [...prev];
                              if (updated[index]) updated[index].showMoreFields = true;
                              return updated;
                            });
                          }}
                        >
                          {t("place.moreFields")} <FiChevronDown className="edit-special-load-toggle-arrow" />
                        </button>
                      </div>
                    )}

                    {deliveryBeforePickupErrorIndexes.date.includes(index) && (
                      <p className="edit-special-load-error delivery-error">{t("errors.deliveryDateBeforePickup")}</p>
                    )}

                    {deliveryBeforePickupErrorIndexes.hour.includes(index) &&
                      !deliveryBeforePickupErrorIndexes.date.includes(index) && (
                        <p className="edit-special-load-error delivery-error">{t("errors.deliveryHourBeforePickup")}</p>
                      )}

                    {deliveryFieldError && index === deliveryLocations.length - 1 && (
                      <p className="edit-special-load-error delivery-error">{deliveryFieldError}</p>
                    )}
                  </div>
                </div>
              ))}

              {deliveryLocations.length < 7 ? (
                <button
                  type="button"
                  className="add-location-btn"
                  onClick={() =>
                    setDeliveryLocations([
                      ...deliveryLocations,
                      {
                        location: "",
                        date: "",
                        hour: "",

                        // 🆕 point-level dimensions
                        length: "",
                        width: "",
                        height: "",
                        lengthError: "",
                        widthError: "",
                        heightError: "",

                        placeId: null,
                        address: "",
                        postalCode: "",
                        city: "",
                        country: "",
                        gMapsLink: "",
                        coordinates: "",
                        contactPhone: "",

                        // 🆕 reference (optional)
                        reference: "",

                        loadingAccess: null, // ✅ add

                        // 🆕 add
                        handlingEquipment: null,
                        handlingEquipmentOther: "",

                        showMoreFields: false,
                      },
                    ])
                  }
                >
                  {t("buttons.addDelivery")}
                </button>
              ) : (
                <p className="edit-special-load-error-btn delivery-error">{t("errors.maxDeliveries")}</p>
              )}
            </div>
          </div>

          {/* 🆕 Notes for Subcontracted (ONLY when carrierId !== companyId) */}
          {showNotes4subcontracted && (
            <div className="edit-special-load-field-group">
              <label className="edit-special-load-label">
                {t("labels.notesForSubcontracted")}
              </label>

              <textarea
                rows={3}
                placeholder={t("notes.subcontractedPlaceholder")}
                value={notes4subcontracted}
                onChange={(e) => {
                  const v = e.target.value ?? "";
                  if (v.length <= 2000) {
                    setNotes4subcontracted(v);
                    if (notes4subcontractedError) setNotes4subcontractedError("");
                  } else {
                    setNotes4subcontractedError(t("notes.maxCharsError"));
                  }
                }}
                className={`edit-special-load-input ${notes4subcontractedError ? "input-error" : ""}`}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                  opacity: 0.75,
                }}
              >
                <span>{t("notes.subcontractedVisibleHint")}</span>
                <span>{t("notes.counter", { count: (notes4subcontracted || "").length })}</span>
              </div>

              {notes4subcontractedError && (
                <p className="edit-special-load-error">{notes4subcontractedError}</p>
              )}
            </div>
          )}

          {/* 📝 Notes for Driver */}
          <div className="edit-special-load-field-group">
            <label className="edit-special-load-label">{t("labels.notesForDriver")}</label>
            <textarea
              rows={3}
              placeholder={t("notes.placeholder")}
              value={notes4driver}
              onChange={(e) => {
                const v = e.target.value ?? "";
                if (v.length <= 2000) {
                  setNotes4driver(v);
                  if (notes4driverError) setNotes4driverError("");
                } else {
                  setNotes4driverError(t("notes.maxCharsError"));
                }
              }}
              className={`edit-special-load-input ${notes4driverError ? "input-error" : ""}`}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, opacity: 0.75 }}>
              <span>{t("notes.visibleHint")}</span>
              <span>{t("notes.counter", { count: notes4driver.length })}</span>
            </div>
            {notes4driverError && <p className="edit-special-load-error">{notes4driverError}</p>}
          </div>

          {fieldValidationError && <div className="edit-special-load-global-error">{fieldValidationError}</div>}

          <div className="edit-special-load-button-group">
            <button type="button" className="edit-special-load-cancel-button" onClick={() => navigate("/loads")}>
              {t("actions.cancel")}
            </button>
            <button type="submit" className="edit-special-load-save-button" disabled={isSaving}>
              {isSaving ? t("actions.saving") : t("actions.saveChanges")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSpecialLoad;
